import { pubInline, pubText, pubPath, pubRich, pubFormat } from "../i18n/publicTranslation";
import { PublicLanguageLinks } from './common/LanguageSwitcher';
import StoryCreationWays from './marketing/StoryCreationWays';
import HomeVoiceShowcase from './marketing/HomeVoiceShowcase';
import { homeVoices } from '../data/homeVoices';
import HomeSectionDivider from './common/HomeSectionDivider';
import { homepageFAQData } from './data/faqs';
import StoreRatings from './common/StoreRatings';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Button, Typography, Box, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import './HomePage.css';
import "swiper/css";
import "swiper/css/effect-cards";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination, Parallax, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { KeyboardArrowLeft, KeyboardArrowRight, PlayArrow, ExpandMore, Close, FileDownload, AccessTime } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useLocation } from 'react-router-dom';
import SEO from './SEO';
import { getRouteConfig } from '../util/routeConfig';
import { useLocalizedRoute } from '../hooks/useLocalizedRoute';
import { useLanguage } from '../contexts/LanguageContext';
import {
  styleData,
  stories,
  generateAudioJsonLd,
  generateMusicJsonLd,
  generateBreadcrumbJsonLd,
  generatePageSectionsJsonLd,
  generateFAQJsonLd,
  generateOrganizationJsonLd,
  generateHowToJsonLd,
  generateSoftwareAppJsonLd,
} from '../util/showcaseData';
import {
  trackDownloadEvent,
  trackSwiperInteraction,
  trackStoryClickEvent,
} from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import FadeVideo, { SOFT_MEDIA_EDGE_MASK } from './common/FadeVideo';
import MusicPlayer from './common/MusicPlayer';
import MusicalCarousel from './MusicalCarousel';
import FadeInSection from './FadeInSection';
import OneLinkDownloadButton, { FABLE_ONELINK_URL } from './OneLinkDownloadButton';
import ProgressiveImage from './ProgressiveImage';
import { getMediumThumbnailLoadingImage } from '../util/thumbnailLoadingImages';

const FABLE_APP_STORE_URL = 'https://apps.apple.com/us/app/fable-the-ai-story-generator/id6497625686';
const FABLE_GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.fableapp';
const FABLE_QR_CODE_SRC = '/download/fable-onelink-qr.svg?v=20260612';
const FABLE_QR_CODE_FALLBACK_SRC = '/download/fable-qr.svg?v=20260612';

const homeCarouselNavButtonSx = {
  minWidth: { xs: 96, sm: 104 },
  minHeight: { xs: 36, sm: 38 },
  px: { xs: 1.5, sm: 1.75 },
  borderRadius: '999px',
  border: '1px solid rgba(216,163,99,.24)',
  background: 'rgba(216,163,99,.055)',
  color: 'rgba(244,201,133,.88)',
  boxShadow: 'none',
  backdropFilter: 'blur(8px)',
  textTransform: 'none',
  fontSize: { xs: '.78rem', sm: '.82rem' },
  lineHeight: 1,
  fontWeight: 700,
  letterSpacing: '.01em',
  transition: 'background .2s ease, border-color .2s ease, color .2s ease, transform .2s ease, box-shadow .2s ease',
  '& .MuiButton-startIcon': { marginInlineEnd: .35 },
  '& .MuiButton-endIcon': { marginInlineStart: .35 },
  '&:hover': {
    background: 'rgba(216,163,99,.12)',
    borderColor: 'rgba(216,163,99,.42)',
    color: '#F4C985',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 18px rgba(0,0,0,.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: 'none',
  },
} as const;

const heroImages = [
  '/hero/Home-Main.jpeg',
  '/hero/AI-Story-Generator-For-Kids.jpeg',
  '/hero/AI-Story-Builder-For-Creators.jpeg',
  '/hero/Generate-Epic-Anime-Adventures.jpeg',
  '/hero/Create-Your-Own-Bedtime-Story.jpeg'
];
const featuredImages = [
  '/hero/Home-Main.jpeg',
  '/hero/Create-a-Short-Bedtime-Story-in-Seconds.jpeg',
  '/hero/Never-Run-Out-of-Story-Ideas-Again.jpeg',
  '/hero/Make-Your-Child-the-Hero-of-Their-Story.jpeg',
  '/hero/Create-Print-Ship-Your-Own-Storybooks-with-No-Experience.jpeg'
];

// Hero badge gradients and shadows matching each hero image theme
const heroBadgeStyles = [
  { gradient: 'linear-gradient(30deg, #8B4513, #1E3A5F)', shadow: '0 4px 16px rgba(139, 69, 19, 0.5), 0 2px 8px rgba(30, 58, 95, 0.3)' }, // pirate - brown to navy
  { gradient: 'linear-gradient(30deg, #014421, #8B4513)', shadow: '0 4px 16px rgba(1, 68, 33, 0.5), 0 2px 8px rgba(139, 69, 19, 0.3)' }, // tribal - forest green to brown
  { gradient: 'linear-gradient(30deg, #1E90FF, #FF6B35)', shadow: '0 4px 16px rgba(30, 144, 255, 0.5), 0 2px 8px rgba(255, 107, 53, 0.3)' }, // space - blue to orange
  { gradient: 'linear-gradient(30deg, #FF69B4, #87CEEB)', shadow: '0 4px 16px rgba(255, 105, 180, 0.5), 0 2px 8px rgba(135, 206, 235, 0.3)' }, // bubble - pink to sky blue
  { gradient: 'linear-gradient(30deg, #DC143C, #FFD700)', shadow: '0 4px 16px rgba(220, 20, 60, 0.5), 0 2px 8px rgba(255, 215, 0, 0.3)' }, // knight/dragon - red to gold
];

const heroDownloadButtonStyles = [
  {
    background: 'linear-gradient(135deg, #F0C85B 0%, #C78A2D 50%, #7A431F 100%)',
    hoverBackground: 'linear-gradient(135deg, #FFD76D 0%, #D99A38 50%, #8A4E27 100%)',
    shadow: '0 10px 28px rgba(199, 138, 45, 0.36), 0 6px 18px rgba(122, 67, 31, 0.22)',
    hoverShadow: '0 14px 36px rgba(199, 138, 45, 0.48), 0 8px 22px rgba(122, 67, 31, 0.28)',
    color: '#fff',
  },
  {
    background: 'linear-gradient(135deg, #073D25 0%, #0E6A3F 50%, #2D9864 100%)',
    hoverBackground: 'linear-gradient(135deg, #0A4B2D 0%, #147849 50%, #37A971 100%)',
    shadow: '0 10px 28px rgba(1, 68, 33, 0.38)',
    hoverShadow: '0 14px 36px rgba(1, 68, 33, 0.5)',
    color: '#fff',
  },
  {
    background: 'linear-gradient(135deg, #6C4D8F 0%, #8B6BB5 50%, #B49AE1 100%)',
    hoverBackground: 'linear-gradient(135deg, #76599B 0%, #9878C4 50%, #C1ACEA 100%)',
    shadow: '0 10px 28px rgba(139, 107, 181, 0.36)',
    hoverShadow: '0 14px 36px rgba(139, 107, 181, 0.48)',
    color: '#fff',
  },
  {
    background: 'linear-gradient(135deg, #A98AF4 0%, #B8D7FF 50%, #A6F1F0 100%)',
    hoverBackground: 'linear-gradient(135deg, #B79BFF 0%, #C5E0FF 50%, #B8FAF8 100%)',
    shadow: '0 10px 28px rgba(166, 241, 240, 0.24), 0 6px 18px rgba(169, 138, 244, 0.28)',
    hoverShadow: '0 14px 36px rgba(166, 241, 240, 0.3), 0 8px 22px rgba(169, 138, 244, 0.34)',
    color: '#fff',
  },
  {
    background: 'linear-gradient(135deg, #5E5A66 0%, #7B728A 50%, #A091B2 100%)',
    hoverBackground: 'linear-gradient(135deg, #6A6673 0%, #897F99 50%, #AE9EC0 100%)',
    shadow: '0 10px 28px rgba(123, 114, 138, 0.34)',
    hoverShadow: '0 14px 36px rgba(123, 114, 138, 0.46)',
    color: '#fff',
  },
];

// Lighter text gradients for "Your Story deserves to be told"
const heroTextGradients = [
  'linear-gradient(30deg, #E8C49A, #7BA3D0)', // pirate - light tan to light blue
  'linear-gradient(30deg, #5DBE8A, #C9A86C)', // tribal - sage green to tan
  'linear-gradient(30deg, #64B5F6, #FFB074)', // space - light blue to peach
  'linear-gradient(30deg, #F9A8D4, #A5D8FF)', // bubble - light pink to light sky
  'linear-gradient(30deg, #F87171, #FCD34D)', // knight/dragon - coral to yellow
];

type HeroTextPart = {
  text: string;
  highlight?: boolean;
  breakBefore?: boolean;
};

type HeroMarketingContent = {
  titleParts: HeroTextPart[];
  descriptionParts: HeroTextPart[];
  quote: string;
};

const defaultHeroMarketingContent: HeroMarketingContent = {
  titleParts: [
    { text: 'Reimagine the Art' },
    { text: 'of ', breakBefore: true },
    { text: 'Storytelling', highlight: true },
  ],
  descriptionParts: [
    { text: 'Fable turns your idea into a complete story with ' },
    { text: 'narration, artwork, music, and video', highlight: true },
    { text: '. Built for ' },
    { text: 'parents, authors, educators, and creators', highlight: true },
    { text: ' who want story creation to feel ' },
    { text: 'effortless', highlight: true },
    { text: '.' },
  ],
  quote: 'Personalized storytelling accessible to all',
};

const heroMarketingContentByPath: Record<string, HeroMarketingContent> = {
  '/': defaultHeroMarketingContent,
  '/bedtime-stories-for-kids': {
    titleParts: [
      { text: 'Make Your Child the ' },
      { text: 'Hero', highlight: true },
      { text: ' of Their Story' },
    ],
    descriptionParts: [
      { text: 'Turn their name, photo, and favorite ideas into ' },
      { text: 'stories they feel are their own', highlight: true },
      { text: '.' },
    ],
    quote: 'Designed for parents, teachers, and creatives',
  },
  '/create-your-own-bedtime-stories': {
    titleParts: [
      { text: 'Create Your Own ' },
      { text: 'Bedtime Story', highlight: true },
    ],
    descriptionParts: [
      { text: 'Create a calming bedtime story with ' },
      { text: 'beautiful illustrations, narration, and music', highlight: true },
      { text: ' before lights out.' },
    ],
    quote: 'Limitless creative possibilities',
  },
  '/create-your-own-anime': {
    titleParts: [
      { text: 'Generate Epic ' },
      { text: 'Anime Adventures', highlight: true },
    ],
    descriptionParts: [
      { text: 'Build anime-inspired adventures with ' },
      { text: 'original characters, narration, music, and video', highlight: true },
      { text: '.' },
    ],
    quote: 'Designed for creatives of all backgrounds',
  },
  '/book-publisher': {
    titleParts: [
      { text: 'Create, Print, & Ship Your Own ' },
      { text: 'Storybooks with No Experience', highlight: true },
    ],
    descriptionParts: [
      { text: 'Publishing your own story has never been easier. Creating your first book is ' },
      { text: 'a simple prompt away', highlight: true },
      { text: '.' },
    ],
    quote: 'Personalized storytelling accessible to all',
  },
  '/create-story-books-with-ai': {
    titleParts: [
      { text: 'Replace Expensive Printed ' },
      { text: 'Storybooks with AI', highlight: true },
    ],
    descriptionParts: [
      { text: 'Create the perfect gift with a ' },
      { text: 'personalized story', highlight: true },
      { text: ', and solidify it in time with a beautiful printed keepsake.' },
    ],
    quote: 'A cost-effective solution to printed storybooks',
  },
  '/ai-story-generator': {
    titleParts: [
      { text: 'Never Run Out of ' },
      { text: 'Story Ideas', highlight: true },
      { text: ' Again' },
    ],
    descriptionParts: [
      { text: 'Start with a simple prompt, and turn it into a complete story with ' },
      { text: 'beautiful illustrations, music, narration and more', highlight: true },
      { text: '.' },
    ],
    quote: 'Limitless creative possibilities',
  },
  '/short-bedtime-stories': {
    titleParts: [
      { text: 'Create a Short Bedtime Story ' },
      { text: 'in Seconds', highlight: true },
    ],
    descriptionParts: [
      { text: 'When bedtime is getting late, create a short story that is ' },
      { text: 'quick, high quality, and ready in a few minutes', highlight: true },
      { text: '.' },
    ],
    quote: 'Accessible personalized storytelling for all',
  },
  '/ai-story-generator-free': {
    titleParts: [
      { text: 'AI Story Generator ' },
      { text: 'For Kids', highlight: true },
    ],
    descriptionParts: [
      { text: 'Create kid-friendly illustrated stories with ' },
      { text: 'narration, music, and video', highlight: true },
      { text: ' with minimal effort.' },
    ],
    quote: 'Personalized storytelling accessible to all',
  },
  '/ai-story-writer': {
    titleParts: [
      { text: 'AI Story Builder ' },
      { text: 'For Creators', highlight: true },
    ],
    descriptionParts: [
      { text: 'Build polished illustrated stories from prompts with ' },
      { text: 'characters, scenes, narration, music, and video', highlight: true },
      { text: '.' },
    ],
    quote: 'Designed for creatives of all backgrounds',
  },
  '/classroom-stories-students-want-to-read': {
    titleParts: [
      { text: 'Classroom Stories Students ' },
      { text: 'Actually Want to Read', highlight: true },
    ],
    descriptionParts: [
      { text: 'Turn lessons and goals into personalized stories with ' },
      { text: 'characters your students will care about', highlight: true },
      { text: '.' },
    ],
    quote: 'Designed for parents, teachers, and creatives',
  },
  '/bible-story-generator': {
    titleParts: [
      { text: 'Create Bible Stories ' },
      { text: 'for Kids', highlight: true },
    ],
    descriptionParts: [
      { text: 'Bring faith, morals, and timeless lessons to life through the ' },
      { text: 'art of AI stories', highlight: true },
      { text: '.' },
    ],
    quote: 'Personalized storytelling accessible to all',
  },
};

type HeroProofCopy = {
  scanDownload: string;
  starRating: string;
  downloads: string;
  freeStory: string;
  inMinutes: string;
};

const homeHeroProofCopy: HeroProofCopy = { scanDownload: 'Scan & Download', starRating: 'star rating', downloads: 'downloads', freeStory: 'Free Story', inMinutes: 'in minutes' };

const heroReviewTiles = [
  {
    name: 'Moaksie',
    image: '/reviews/moaksie.jpeg',
    review: "The stories are actually pretty great quality! Created a character and it looks just like my nephew! ❤️",
  },
  {
    name: 'Mcamille D',
    image: '/reviews/mcamille.jpeg',
    review: 'The stories are sweet, the voices are charming, and the whole experience feels magical.',
  },
  {
    name: 'Adam S.',
    image: '/reviews/adam.jpeg',
    review: 'Fun for daughter and dad. It is a great alternative before bed.',
  },
];

const swoosh = require('./images/swoosh_thick.png');
const swooshReverse = require('./images/swoosh_thick_reverse.png');


const nbc = require('./images/nbc.png');
const abc = require('./images/abc.png');
const cbs = require('./images/cbs.png');

// Musical tracks for the carousel - stored in public folder
const princessMusic = '/music/princess.mp3';
const dragonMusic = '/music/dragon.mp3';
const superheroMusic = '/music/superhero.mp3';

// Moral images and data - matching the mobile app structure
const moralsData = [
  {
    id: 'integrity',
    name: 'Integrity',
    label: 'Actions speak louder than words',
    image: require('./images/morals/actions.jpg'),
    description: 'Teaching that our actions define who we are',
    fullDescription: 'Integrity means doing the right thing even when no one is watching. Through Fable\'s personalized stories, children learn that actions speak louder than words. Our AI creates adventures where your child becomes a hero whose honest actions and consistent behavior earn trust and respect from others.',
    color: '#4A90E2'
  },
  {
    id: 'perseverance',
    name: 'Perseverance',
    label: 'Never give up',
    image: require('./images/morals/determination.jpg'),
    description: 'Never giving up when facing challenges',
    fullDescription: 'Perseverance is the key to achieving our dreams. In Fable\'s stories, your child becomes a determined hero who never gives up, even when things get tough. These tales teach children that with persistence and hard work, they can overcome any challenge and achieve their goals.',
    color: '#45B7D1'
  },
  {
    id: 'patience',
    name: 'Patience',
    label: 'Good things take time',
    image: require('./images/morals/patience.jpg'),
    description: 'Learning to wait and be patient',
    fullDescription: 'Patience is a virtue that helps us appreciate life\'s journey. Through Fable\'s personalized stories, children learn that good things come to those who wait. Your child becomes a wise character who discovers that patience leads to better outcomes and greater happiness.',
    color: '#DDA0DD'
  },
  {
    id: 'reliability',
    name: 'Reliability',
    label: 'Keep your promises',
    image: require('./images/morals/reliable.jpg'),
    description: 'Being someone others can count on',
    fullDescription: 'Reliability builds trust and strong relationships. Fable creates stories where your child learns the importance of keeping promises and being dependable. These adventures show how being reliable makes you a valued friend and trusted person in your community.',
    color: '#96CEB4'
  },
  {
    id: 'kindness',
    name: 'Kindness',
    label: 'Kindness and compassion',
    image: require('./images/morals/kindnesss.jpg'),
    description: 'Teaching the importance of being kind to others',
    fullDescription: 'Kindness is one of the most beautiful virtues we can teach our children. Through Fable\'s personalized stories, children learn how small acts of kindness can make a big difference in the world. Our AI generates stories where your child becomes the hero who helps others, shares with friends, and shows compassion to those in need.',
    color: '#FF6B9D'
  },
  {
    id: 'forgiveness',
    name: 'Forgiveness',
    label: 'Learn to forgive',
    image: require('./images/morals/forgive.jpg'),
    description: 'Understanding the power of forgiveness',
    fullDescription: 'Forgiveness frees our hearts and heals relationships. Fable creates touching stories where your child learns that forgiving others (and themselves) brings peace and happiness. These adventures show that forgiveness is a strength, not a weakness.',
    color: '#FFB6C1'
  },
  {
    id: 'truthfulness',
    name: 'Truthfulness',
    label: 'Always tell the truth',
    image: require('./images/morals/truth.jpg'),
    description: 'Always telling the truth and being honest',
    fullDescription: 'Honesty is the cornerstone of trust and integrity. In Fable\'s stories, your child learns that telling the truth, even when it\'s difficult, always leads to the best outcomes. Our AI creates adventures where honesty saves the day and builds stronger relationships.',
    color: '#A8E6CF'
  },
  {
    id: 'respect',
    name: 'Respect',
    label: 'Respect others',
    image: require('./images/morals/respect.jpg'),
    description: 'Showing respect for others and ourselves',
    fullDescription: 'Respect forms the foundation of all good relationships. Through Fable\'s personalized stories, children learn to respect their parents, teachers, friends, and themselves. Our AI creates adventures where your child discovers that treating others with respect leads to wonderful friendships and success.',
    color: '#8E44AD'
  },
  {
    id: 'friendship',
    name: 'Friendship',
    label: 'Be a good friend',
    image: require('./images/morals/friendship.jpg'),
    description: 'Understanding the value of true friendship',
    fullDescription: 'Friendship is a treasure that enriches our lives in countless ways. Fable creates magical stories where your child learns about loyalty, trust, and the joy of having good friends. Through adventures with companions, children discover how to be a good friend and how friendships can overcome any obstacle.',
    color: '#4ECDC4'
  },
  {
    id: 'mindfulness',
    name: 'Mindfulness',
    label: 'Think before you act',
    image: require('./images/morals/mindful.jpg'),
    description: 'Learning to pause and think carefully',
    fullDescription: 'Mindfulness teaches children to think before they act and consider the consequences of their choices. Through Fable\'s stories, your child learns to be aware of their thoughts and feelings, make thoughtful decisions, and treat others with consideration.',
    color: '#F39C12'
  },
  {
    id: 'contentment',
    name: 'Contentment',
    label: 'You can\'t always get what you want',
    image: require('./images/morals/want.jpg'),
    description: 'Finding happiness with what we have',
    fullDescription: 'Contentment helps children appreciate what they have rather than always wanting more. Fable creates stories where your child learns that happiness comes from within and that being grateful for what we have brings true joy and peace.',
    color: '#E67E22'
  },
  {
    id: 'sharing',
    name: 'Sharing',
    label: 'Sharing is caring',
    image: require('./images/morals/sharing.jpg'),
    description: 'Learning the joy of sharing with others',
    fullDescription: 'Sharing brings happiness to both the giver and receiver. Fable\'s stories show your child how sharing toys, food, or even a smile can create magical moments. Through these personalized adventures, children learn that sharing multiplies joy and creates stronger bonds with others.',
    color: '#FECA57'
  },
  {
    id: 'fairness',
    name: 'Fairness',
    label: 'Always be fair',
    image: require('./images/morals/fair.jpg'),
    description: 'Treating everyone equally and justly',
    fullDescription: 'Fairness teaches children to treat everyone equally and stand up for what is right. Through Fable\'s stories, your child learns about justice, equality, and making sure everyone gets a fair chance. These adventures show the importance of being fair in games, friendships, and life.',
    color: '#27AE60'
  },
  {
    id: 'example',
    name: 'Being a Good Example',
    label: 'Be a good example for others',
    image: require('./images/morals/example.jpg'),
    description: 'Leading by example and inspiring others',
    fullDescription: 'Being a good example means showing others the right way to behave through your own actions. Fable creates stories where your child becomes a positive role model who inspires others to do good deeds and make positive choices.',
    color: '#34495E'
  }
];

const newsImages = [
    { channel: nbc, url: "https://fwnbc.marketminute.com/article/getnews-2025-2-24-innovative-ai-app-fable-makes-personalized-storytelling-accessible-to-all" },
    { channel: abc, url: "https://wkow.marketminute.com/article/getnews-2025-2-24-innovative-ai-app-fable-makes-personalized-storytelling-accessible-to-all" },
    { channel: cbs, url: "https://markets.financialcontent.com/fourptsmedia.kutv/article/getnews-2025-2-24-innovative-ai-app-fable-makes-personalized-storytelling-accessible-to-all/" }
];







// Helper function to add UTM parameters to URLs
const addUTMParams = (url: string, source: string, medium: string = 'web', campaign?: string) => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('utm_source', source);
  urlObj.searchParams.set('utm_medium', medium);
  if (campaign) {
    urlObj.searchParams.set('utm_campaign', campaign);
  }
  return urlObj.toString();
};

// Get traffic source from referrer or UTM parameters
const getTrafficSource = () => {
  // Check for UTM parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  if (utmSource) return utmSource;

  // Check referrer
  const referrer = document.referrer;
  if (!referrer) return 'thefableapp.com';

  // Strip https:// and get clean hostname
  const hostname = new URL(referrer).hostname.toLowerCase();
  const cleanHostname = hostname.replace(/^https?:\/\//, '').replace(/^www\./, '');

  // landing_page can be tik tok or linkedned and direct link from phone or something.
  return cleanHostname && cleanHostname.length > 0 ? cleanHostname : 'thefableapp.com';
};

// Language data for the grid
const languageData = [
  { id: 'english', name: 'English', image: require('./images/languages/english.jpeg'), description: 'Global communication language', isFullySupported: true },
  { id: 'spanish', name: 'Spanish', image: require('./images/languages/spanish.jpeg'), description: 'Widely spoken romance language', isFullySupported: false },
  { id: 'french', name: 'French', image: require('./images/languages/french.jpeg'), description: 'Language of love and culture', isFullySupported: false },
  { id: 'german', name: 'German', image: require('./images/languages/german.jpeg'), description: 'Precision and engineering', isFullySupported: false },
  { id: 'italian', name: 'Italian', image: require('./images/languages/italian.jpeg'), description: 'Art and culinary excellence', isFullySupported: false },
  { id: 'portuguese', name: 'Portuguese', image: require('./images/languages/portuguese.jpeg'), description: 'Rich cultural heritage', isFullySupported: false },
  { id: 'chinese', name: 'Chinese', image: require('./images/languages/chinese.jpeg'), description: 'Ancient wisdom and modernity', isFullySupported: false },
  { id: 'japanese', name: 'Japanese', image: require('./images/languages/japan.jpeg'), description: 'Innovation and tradition', isFullySupported: false },
  { id: 'korean', name: 'Korean', image: require('./images/languages/korean.jpeg'), description: 'Dynamic culture and technology', isFullySupported: false },
  { id: 'arabic', name: 'Arabic', image: require('./images/languages/arabic.jpeg'), description: 'Heritage and eloquence', isFullySupported: false },
  { id: 'hindi', name: 'Hindi', image: require('./images/languages/hindi.jpeg'), description: 'Diverse and vibrant culture', isFullySupported: false },
  { id: 'russian', name: 'Russian', image: require('./images/languages/russian.jpeg'), description: 'Literature and grandeur', isFullySupported: false },
  { id: 'bulgarian', name: 'Bulgarian', image: require('./images/languages/bulgarian.jpeg'), description: 'Rich Slavic heritage', isFullySupported: false },
  { id: 'czech', name: 'Czech', image: require('./images/languages/czech.jpeg'), description: 'Heart of Central Europe', isFullySupported: false },
  { id: 'dutch', name: 'Dutch', image: require('./images/languages/dutch.jpeg'), description: 'Language of innovation', isFullySupported: false },
  { id: 'finnish', name: 'Finnish', image: require('./images/languages/finnish.jpeg'), description: 'Land of a thousand lakes', isFullySupported: false },
  { id: 'greek', name: 'Greek', image: require('./images/languages/greek.jpeg'), description: 'Cradle of civilization', isFullySupported: false },
  { id: 'indonesian', name: 'Indonesian', image: require('./images/languages/indonesian.jpeg'), description: 'Island nation diversity', isFullySupported: false },
  { id: 'polish', name: 'Polish', image: require('./images/languages/polish.jpeg'), description: 'Resilient and proud culture', isFullySupported: false },
  { id: 'romanian', name: 'Romanian', image: require('./images/languages/romanian.jpeg'), description: 'Romance language beauty', isFullySupported: false },
  { id: 'thai', name: 'Thai', image: require('./images/languages/thai.jpeg'), description: 'Smiles and traditions', isFullySupported: false },
  { id: 'turkish', name: 'Turkish', image: require('./images/languages/turkish.jpeg'), description: 'Bridge between continents', isFullySupported: false },
  { id: 'ukrainian', name: 'Ukrainian', image: require('./images/languages/ukranian.jpeg'), description: 'Strong and resilient spirit', isFullySupported: false },
  { id: 'vietnamese', name: 'Vietnamese', image: require('./images/languages/vietnamese.jpeg'), description: 'Tonal language richness', isFullySupported: false },
];

const HomePage: React.FC = React.memo(() => {
  const location = useLocation();
  const { routeConfig: localizedRouteConfig, alternateUrls } = useLocalizedRoute();
  const { currentLanguage } = useLanguage();
  // Fallback to English route config if no localized config found
  const routeConfig = localizedRouteConfig || getRouteConfig(location.pathname);

  // Musical carousel state
  const [currentMusicalIndex, setCurrentMusicalIndex] = React.useState(1);

  // Musical tracks array (matches featuredMusicals order)
  const musicalTracks = [
    { src: dragonMusic, title: 'Battle of the Bands', subtitle: 'Epic rock adventure to save the world' },
    { src: princessMusic, title: "Princess Daisy's Harvest Moon Ball", subtitle: 'A magical country-themed ball' },
    { src: superheroMusic, title: 'Power-Up Champions', subtitle: 'Action-packed superhero musical' }
  ];

  const currentTrack = musicalTracks[currentMusicalIndex];

  // Randomly select a hero index on mount, unless a landing-page route pins one.
  const [randomHeroIndex] = useState(() => Math.floor(Math.random() * heroImages.length));
  const heroIndex = useMemo(() => {
    const configuredIndex = routeConfig.heroThemeIndex;
    if (typeof configuredIndex === 'number' && Number.isFinite(configuredIndex)) {
      return Math.max(0, Math.min(heroImages.length - 1, configuredIndex));
    }
    return randomHeroIndex;
  }, [routeConfig.heroThemeIndex, randomHeroIndex]);

  const heroImage = useMemo(() => {
    return routeConfig.heroImage || heroImages[heroIndex];
  }, [heroIndex, routeConfig.heroImage]);

  const heroMarketingContent = useMemo(() => {
    return heroMarketingContentByPath[routeConfig.path] || defaultHeroMarketingContent;
  }, [currentLanguage, routeConfig.path]);
  const isHomeHero = routeConfig.path === '/';
  const translatedHeroParts = (parts: HeroTextPart[], className: string) => {
    const slots: React.ReactNode[] = [];
    const template = parts.map(part => {
      const prefix = part.breakBefore ? ' ' : '';
      if (!part.highlight) return prefix + part.text;
      const token = `<em${slots.length}>${part.text}</em${slots.length}>`;
      slots.push(<Box component="span" className={className}>{pubText(part.text)}</Box>);
      return prefix + token;
    }).join('');
    return pubInline(template, slots as React.ReactElement[]);
  };


  const heroProofCopy = useMemo(() => {
    return homeHeroProofCopy;
  }, [currentLanguage]);

  const featuredImage = useMemo(() => {
    return routeConfig.featuredImage || featuredImages[heroIndex];
  }, [heroIndex, routeConfig.featuredImage]);

  // Preload all hero and feature images to cache them
  useEffect(() => {
    heroImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
    featuredImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const storySwiperRef = useRef<any>(null)
  const styleSwiperRef = useRef<any>(null)
  const [swiperEffect, setSwiperEffect] = useState('coverflow');
  // Material UI breakpoints
  const matchesXs = useMediaQuery('(min-width:0px)');      // Extra small: 0px+
  const matchesSm = useMediaQuery('(min-width:600px)');    // Small: 600px+
  const matchesMd = useMediaQuery('(min-width:900px)');    // Medium: 900px+
  const matchesLg = useMediaQuery('(min-width:1200px)');   // Large: 1200px+
  const matchesXl = useMediaQuery('(min-width:1536px)');   // Extra large: 1536px+
  const observerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHeroImageLoading, setIsHeroImageLoading] = useState(true);
  const [isFeaturedImageLoading, setIsFeaturedImageLoading] = useState(true);
  const [isSwooshLoading, setIsSwooshLoading] = useState(true);
  const [currentHash, setCurrentHash] = useState<string>('');
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<string | false>(false);
  const [showBanner, setShowBanner] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsHeroImageLoading(true);
    setIsFeaturedImageLoading(true);
  }, [heroImage, featuredImage]);

  // Helper function to clear hash and navigate
  const navigateWithoutHash = useCallback((path: string) => {
    // Clear the hash from the current URL if it exists
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    // Navigate to the new path
    navigate(pubPath(path));
  }, [navigate]);

  // Memoize social sizes
  const socialSizes = useMemo(() =>
    !matchesLg ? {height: 60, width: 60} : {height: 80, width: 80},
    [matchesLg]
  );

  // Memoize callbacks
  const onStoryPress = useCallback((story: any, index: number) => {
    trackStoryClickEvent(story.title, index);
    navigateWithoutHash(story.detailPath || `/story-details/${story.styleId}/${story.storyId}`);
  }, [navigateWithoutHash]);

  const handleDownloadClick = useCallback((location: string, platform: 'ios' | 'android' | 'onelink' = 'onelink') => {
    trackDownloadEvent(platform, location);
  }, []);

  const handlePrevClick = useCallback((swiperRef: any) => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  }, []);

  const handleNextClick = useCallback((swiperRef: any) => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  }, []);

  const handleHeroImageLoad = useCallback(() => {
    setIsHeroImageLoading(false);
  }, []);

  const heroImageMaskStyle = useMemo(() => ({
    maskImage: `
      linear-gradient(to right, transparent 0%, black 16%, black 84%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)
    `,
    WebkitMaskImage: `
      linear-gradient(to right, transparent 0%, black 16%, black 84%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)
    `,
    maskComposite: 'intersect',
    WebkitMaskComposite: 'intersect',
  }), []);

  const handleHeroImageError = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    setIsHeroImageLoading(false);
    const target = event.target as HTMLImageElement;
    if (target?.style) {
      target.style.maskImage = heroImageMaskStyle.maskImage;
      target.style.webkitMaskImage = heroImageMaskStyle.WebkitMaskImage as string;
      target.style.maskComposite = heroImageMaskStyle.maskComposite as string;
      target.style.webkitMaskComposite = heroImageMaskStyle.WebkitMaskComposite as string;
    }
  }, [heroImageMaskStyle]);

  const handleQrCodeError = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const target = event.currentTarget;
    if (!target.src.includes('fable-qr.svg')) {
      target.src = FABLE_QR_CODE_FALLBACK_SRC;
    }
  }, []);

  const handleFeaturedImageLoad = useCallback(() => {
    setIsFeaturedImageLoading(false);
  }, []);

  const handleFeaturedImageError = useCallback(() => {
    setIsFeaturedImageLoading(false);
  }, []);

  const handleSwooshLoad = useCallback(() => {
    setIsSwooshLoading(false);
  }, []);

  const handleSwooshError = useCallback(() => {
    setIsSwooshLoading(false);
  }, []);

  const handleFAQChange = useCallback((panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFAQ(isExpanded ? panel : false);

    // Update URL without refreshing page
    if (isExpanded) {
      // Set hash to the panel anchor
      window.history.pushState(null, '', `#${panel}`);
    } else {
      // Remove hash, go to root
      window.history.pushState(null, '', window.location.pathname);
    }
  }, []);


  // Memoize reviews data
  const reviews = useMemo(() => [
    {
        name: "Rousgoose",
        review: "This app is a wonderful way to use technology to engage with your children's imagination and foster creativity.",
        rating: 5
    },
    {
        name: "Mcamille D",
        review: "Fable is absolutely adorable! The stories are sweet, the voices are charming, and the whole experience just feels magical. Love, love, love this app!!",
        rating: 5
    },
    {
        name: "Moaksie",
        review: "The stories are actually pretty great quality! Created a character and it looks just like my nephew! ❤️",
        rating: 5
    },
    {
        name: "Adam S.",
        review: "Fun for daughter and dad! Really enjoy creating personalized AI stories with my daughter. It's a fun alternative to reading books before bed. Highly recommend!",
        rating: 5
    },
    {
        name: "Rhonda H.",
        review: "Fable is pretty awesome so far, this is my first time using this ai app but definitely not my last!",
        rating: 5
    },
    {
        name: "Myra R.",
        review: "Outstanding great help",
        rating: 5
    },
    {
        name: "David D.",
        review: "Awesome",
        rating: 5
    },
    {
        name: "Miguel J.",
        review: "I like this app",
        rating: 5
    },
    {
        name: "Vicky S.",
        review: "Nice",
        rating: 5
    },
    {
        name: "Levi F.",
        review: "Very fun",
        rating: 5
    },
    {
        name: "Tasha M.",
        review: "Very cool",
        rating: 5
    },
    {
        name: "Bobbyjo W.",
        review: "Wounderfull",
        rating: 5
    },
    {
        name: "Sibeal M.",
        review: "Good 💯",
        rating: 5
    }
  ], []);

  // Memoize news slides
  const newsSlides = useMemo(() => (
    <Box className="tertiaryScreenShotContainer">
        <Grid container justifyContent="space-between" spacing={matchesXs && !matchesSm ? 0 : 2}>
            {pubText(newsImages.map((image, index) => (
                <Grid size={{ xs: 3, sm: 4, md: 4 }} key={index} className="tertiaryScreenShotContainer">
                    <a href={pubPath(image.url)}>
                        <img
                            className="tertiaryScreen news-card-image"
                            src={image.channel}
                            alt={pubText("Fable AI story generator featured in media outlet")}
                            loading="lazy"
                            width="200"
                            height="150"
                        />
                    </a>
                </Grid>
            )))}
        </Grid>
    </Box>
  ), [matchesXs, matchesSm]);

  // Memoize story slides
  const storySlides = useMemo(() => (
    <>
        {pubText(stories.map((story, index) => {
          const fallbackImage = getMediumThumbnailLoadingImage(
            story.storyId || story.title,
          );
          return (
              <SwiperSlide key={index}>
                  <Box
                      className="secondaryScreenShotContainer"
                      onClick={() => onStoryPress(story, index)}
                      sx={{
                          cursor: 'pointer',
                          transition: 'transform 0.3s ease',
                          boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)',
                          '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: '0 8px 15px rgba(0, 0, 0, 0.6)'
                          },
                      }}
                      >
                      <ProgressiveImage
                          src={story.imageUrl}
                          fallbackSrc={fallbackImage}
                          seed={story.storyId || story.title}
                          alt={pubFormat("__VALUE_0__ - AI generated bedtime story example", [story.title])}
                          sx={{
                            width: 300,
                            height: 550,
                            alignSelf: 'center',
                            borderRadius: '16px',
                          }}
                      />
                      <Typography className={'story'}>{pubText(story.title)}</Typography>
                  </Box>
              </SwiperSlide>
          );
        }))}
    </>
  ), [onStoryPress]);

  // Memoize style slides
  const styleSlides = useMemo(() => (
    <>
      {pubText(styleData.map((style, index) => (
        <SwiperSlide key={`style-${index}`}>
          <Box
            sx={{
              background: 'transparent',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
            style={{
              margin: '8px 18px',
            }}
          >
            <Button
            className="voice-container"
              onClick={(e) => {
                e.stopPropagation();
                navigateWithoutHash(`/styles/${style.id}`);
              }}
              sx={{
                width: '100%',
                aspectRatio: '1',
                position: 'relative',
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                background: 'transparent',
                transition: 'box-shadow 0.3s ease',
                cursor: 'pointer',
                padding: 0,
                minWidth: 'unset',
                border: 'none',
                '&:hover': {
                  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
                  background: 'transparent'
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <img
                src={style.image}
                alt={pubFormat("__VALUE_0__ AI story art style example", [style.label])}
                loading="lazy"
                width="300"
                height="400"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'scale(1.1)',
                  transition: 'transform 0.3s ease',
                  filter: 'brightness(1.05) contrast(1.05)'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '5%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: 'blur(4px)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  zIndex: 2,
                  whiteSpace: 'nowrap',
                  fontSize: '0.9rem',
                  color: '#fff',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}
              >
                {pubText(style.label)}
              </Box>
            </Button>
          </Box>
        </SwiperSlide>
      )))}
    </>
  ), [navigateWithoutHash]);



  useEffect(() => {
    setSwiperEffect(matchesLg ? 'coverflow' : 'cards');
  }, [matchesLg]);

  useEffect(() => {
    const scrollToHash = (retryCount = 0) => {
      const hash = window.location.hash.substring(1);
      setCurrentHash(hash); // Update current hash state

      if (hash) {
        // Regular hash handling for other sections first
        const element = document.getElementById(hash);
        if (element) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';

          // Match native anchor scrolling and the walkthrough's header clearance.
          if (element.matches('.creation-walkthrough')) {
            element.scrollIntoView({ behavior, block: 'start' });
            return;
          }

          // Get the actual header height dynamically
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 90; // fallback to 90px

          const elementPosition = element.offsetTop - headerHeight - 20;

          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            window.scrollTo({ top: elementPosition, behavior });
          });
        } else if (retryCount < 3) {
          // If element not found, retry after a short delay (content might still be loading)
          setTimeout(() => scrollToHash(retryCount + 1), 100);
        }
      }
    };

    const scrollToHashWithDelay = () => {
      setTimeout(() => scrollToHash(), 150);
    };

    const handleHashChange = () => {
      scrollToHash();
    };

    window.addEventListener('hashchange', handleHashChange);

    // For initial page load, add delay and retry mechanism
    if (window.location.hash) {
      scrollToHashWithDelay();
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  //This will start auto scroll when slider becomes visible
  useEffect(() => {
    // Observer to track visibility of the slider
    const observer = new IntersectionObserver(
        ([entry]) => {
            setIsVisible(entry.isIntersecting);
        },
        { threshold: 0.5 } // Adjust threshold as needed
    );

    if (observerRef.current) {
        observer.observe(observerRef.current);
    }

    return () => {
        if (observerRef.current) {
            observer.unobserve(observerRef.current);
        }
    };
}, []);

  // // Auto-hide/show banner on scroll
  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (window.scrollY > 250 && showBanner) {
  //       setShowBanner(false);
  //     } else if (window.scrollY < 50 && !showBanner) {
  //       setShowBanner(true);
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);

  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, [showBanner]);

  // Get traffic source once on component mount
  const trafficSource = useMemo(() => getTrafficSource(), []);

  const canonicalPath = location.pathname;

  const seoData = useMemo(() => ({
    title: routeConfig.seoTitle || routeConfig.title,
    description: routeConfig.description,
    keywords: pubText(routeConfig.keywords) + pubText(", AI story generator, AI book generator, generative AI stories, fairy tales for kids, adventure stories"),
    ogTitle: routeConfig.ogTitle,
    ogDescription: routeConfig.ogDescription,
    ogType: "website",
    ogUrl: `https://thefableapp.com${canonicalPath}`,
    twitterTitle: routeConfig.twitterTitle,
    twitterDescription: routeConfig.twitterDescription,
    canonical: `https://thefableapp.com${canonicalPath}`,
    structuredData: [
      generateBreadcrumbJsonLd(currentHash, routeConfig.breadcrumbName),
      generatePageSectionsJsonLd(),
      generateAudioJsonLd(homeVoices),
      generateMusicJsonLd(),
      generateFAQJsonLd(homepageFAQData),
      generateOrganizationJsonLd(),
      generateHowToJsonLd(),
      generateSoftwareAppJsonLd()
    ]
  }), [routeConfig, canonicalPath, currentHash]);

  // Generate social media URLs with UTM parameters
  const socialUrls = useMemo(() => ({
    tiktok: addUTMParams('https://www.tiktok.com/@thefableapp', 'website', 'social', 'homepage'),
    instagram: addUTMParams('https://www.instagram.com/thefableapp', 'website', 'social', 'homepage'),
    youtube: addUTMParams('https://www.youtube.com/@thefableapp/shorts', 'website', 'social', 'homepage'),
    linkedin: addUTMParams('https://www.linkedin.com/company/the-fable-app', 'website', 'social', 'homepage'),
    x: addUTMParams('https://x.com/TheFableApp', 'website', 'social', 'homepage'),
    linkedinPost: addUTMParams('https://www.linkedin.com/feed/update/urn:li:activity:7218829300907536384', 'website', 'social', 'homepage')
  }), []);

  const handleMyStoriesClick = useCallback(() => {
    if (isAuthenticated) {
      navigateWithoutHash('/my-stories');
    } else {
      navigateWithoutHash('/signin');
    }
  }, [isAuthenticated, navigateWithoutHash]);

  // Separate effect for FAQ hash handling
  useEffect(() => {
    const handleFAQHash = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        // Check if hash matches an FAQ anchor ID
        const faqItem = homepageFAQData.find(item => item.anchorId === hash);
        if (faqItem) {
          // Expand the FAQ accordion
          setExpandedFAQ(hash);

          // Scroll to specific FAQ accordion
          setTimeout(() => {
            const faqElement = document.getElementById(hash);
            if (faqElement) {
              const header = document.querySelector('header');
              const headerHeight = header ? header.offsetHeight : 90;
              const elementPosition = faqElement.offsetTop - headerHeight - 90;

              window.scrollTo({ top: elementPosition, behavior: 'smooth' });
            }
          }, 200);
        }
      }
    };

    // Handle initial load
    handleFAQHash();

    window.addEventListener('hashchange', handleFAQHash);

    return () => {
      window.removeEventListener('hashchange', handleFAQHash);
    };
  }, []);

  return (
      <Box className="container" flexDirection={"column"} sx={{ overflowX: 'hidden' }}>
          <SEO
            title={pubText(seoData.title)}
            description={pubText(seoData.description)}
            keywords={seoData.keywords}
            ogTitle={seoData.ogTitle}
            ogDescription={seoData.ogDescription}
            ogType={seoData.ogType}
            ogUrl={seoData.ogUrl}
            ogImage={isHomeHero ? 'https://thefableapp.com/hero/Home-Main.jpeg' : undefined}
            imageAlt={pubText(
                isHomeHero ? 'A young knight and friendly dragon on an open Fable storybook, surrounded by narration, music, images and video' : undefined
            )}
            twitterTitle={seoData.twitterTitle}
            twitterDescription={seoData.twitterDescription}
            dateModified={location.pathname === '/create-your-own-anime' ? '2026-11-02' : isHomeHero ? '2026-10-29' : '2026-10-28'}
            structuredData={seoData.structuredData}
            hreflang={alternateUrls.map(alt => ({ lang: alt.lang, url: alt.url }))}
            canonical={seoData.canonical}
          />
          {/* Christmas Special Banner */}
          <Box
            sx={{
              width: '100%',
              background: 'linear-gradient(90deg, #165B33 0%, #BB2528 50%, #165B33 100%)',
              color: '#fff',
              textAlign: 'center',
              position: 'fixed',
              top: { xs: '70px', sm: '64px' },
              left: 0,
              zIndex: 999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
              borderBottom: '2px solid #FFD700',
              backdropFilter: 'blur(10px)',
              transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
              transform: showBanner ? 'translateY(0)' : 'translateY(-100%)',
              opacity: showBanner ? 1 : 0,
              pointerEvents: showBanner ? 'auto' : 'none',
            }}
          >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: '12px 16px',
                  textAlign: "start",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >{pubText(" CHRISTMAS SPECIAL: 20% OFF ANNUAL PLANS + 10% OFF WEB PURCHASES ")}</Typography>

              <Button
                size="small"
                variant="contained"
                onClick={() => navigateWithoutHash('/payment')}
                sx={{
                  backgroundColor: '#fff',
                  color: '#165B33',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  padding: '4px 12px',
                  minWidth: 'auto',
                  borderRadius: '24px',
                  whiteSpace: 'nowrap',
                  border: '2px solid #E8F4F8',
                  boxShadow: '0 2px 8px rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: '#E8F4F8',
                    color: '#BB2528',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 15px rgba(255, 255, 255, 0.5)'
                  }
                }}
              >{pubText(" CLAIM GIFT ")}</Button>

              <IconButton
                size="small"
                onClick={() => setShowBanner(false)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  padding: '8px 8px 8px 8px',
                  marginInlineStart: { xs: 0, sm: 1 },
                  '&:hover': { color: '#FFD700' }
                }}
              >
                <Close fontSize="small" sx={{ fontSize: '1.4rem' }} />
              </IconButton>
            </Box>
          <Box className="landing-hero-redesign" id="home">
            <Grid
              container
              className="landing-hero-redesign__content"
              columnSpacing={{ xs: 0, lg: 6 }}
              rowSpacing={{ xs: 4, lg: 0 }}
              direction={{ xs: 'column', lg: 'row' }}
              sx={{ paddingTop: showBanner ? { xs: '136px', md: '142px', lg: '174px' } : { xs: '82px', md: '82px', lg: '120px' } }}
            >
              <Grid size={{ xs: 12, lg: 6 }} className="landing-hero-redesign__copy">
                <Typography
                  component="h1"
                  className={`landing-hero-redesign__title${isHomeHero ? ' landing-hero-redesign__title--home' : ''}`}
                >
                  {currentLanguage !== 'en' ? translatedHeroParts(heroMarketingContent.titleParts, 'landing-hero-redesign__highlight') : pubText(heroMarketingContent.titleParts.map((part, index) => (
                    <React.Fragment key={`${part.text}-${index}`}>
                      {pubText(part.breakBefore && <br />)}
                      <Box
                        component="span"
                        className={part.highlight ? 'landing-hero-redesign__highlight' : undefined}
                      >
                        {pubText(part.text)}
                      </Box>
                    </React.Fragment>
                  )))}
                </Typography>
                <Typography component="p" className="landing-hero-redesign__subtitle">
                  {currentLanguage !== 'en' ? translatedHeroParts(heroMarketingContent.descriptionParts, 'landing-hero-redesign__subtitle-highlight') : pubText(heroMarketingContent.descriptionParts.map((part, index) => (
                    <Box
                      key={`${part.text}-${index}`}
                      component="span"
                      className={part.highlight ? 'landing-hero-redesign__subtitle-highlight' : undefined}
                    >
                      {pubText(part.text)}
                    </Box>
                  )))}
                </Typography>

                {routeConfig.path === '/create-your-own-anime' && (
                  <Button component={Link} to={pubPath("/blog/anime-story-ideas")} className="landing-hero-redesign__ideas-link" endIcon={<KeyboardArrowRight />}>
                    {pubText("Explore 20 original anime story ideas")}
                  </Button>
                )}

                <Box className="landing-hero-redesign__downloads" id="download">
                  <Box
                    component="a"
                    href={pubPath(FABLE_APP_STORE_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleDownloadClick('hero_app_store', 'ios')}
                    className="landing-hero-redesign__store-badge"
                    aria-label={pubText("Download Fable on the App Store")}
                  >
                    <Box component="img" src="/download/apple.svg" alt={pubText("Download on the App Store")} />
                  </Box>
                  <Box
                    component="a"
                    href={pubPath(FABLE_GOOGLE_PLAY_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleDownloadClick('hero_google_play', 'android')}
                    className="landing-hero-redesign__store-badge"
                    aria-label={pubText("Get Fable on Google Play")}
                  >
                    <Box component="img" src="/download/google.svg" alt={pubText("Get it on Google Play")} />
                  </Box>
                  <Box
                    component="a"
                    href={pubPath(FABLE_ONELINK_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleDownloadClick('hero_qr')}
                    className="landing-hero-redesign__qr-card"
                    aria-label={pubText("Scan QR code to download Fable")}
                  >
                    <Box component="span">{pubText(heroProofCopy.scanDownload)}</Box>
                    <Box className="landing-hero-redesign__qr-popover">
                      <Box className="landing-hero-redesign__qr-frame">
                        <Box
                          component="img"
                          className="landing-hero-redesign__qr-code"
                          src={FABLE_QR_CODE_SRC}
                          alt={pubText("QR code to download Fable")}
                          onError={handleQrCodeError}
                        />
                        <Box component="img" className="landing-hero-redesign__qr-frame-image" src="/qr-frame.png" alt={pubText("")} aria-hidden="true" />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box className="landing-hero-redesign__proof-row">
                  <StoreRatings language={currentLanguage} label={pubText(heroProofCopy.starRating)} />
                  {pubText([
                    { icon: <FileDownload />, value: '25k+', label: heroProofCopy.downloads },
                    { icon: <AccessTime />, value: heroProofCopy.freeStory, label: heroProofCopy.inMinutes },
                  ].map(proof => (
                    <Box key={proof.label} className="landing-hero-redesign__proof-item">
                      <Box component="span" className="landing-hero-redesign__proof-icon">{pubText(proof.icon)}</Box>
                      <Box>
                        <Box className="landing-hero-redesign__proof-value">{pubText(proof.value)}</Box>
                        <Box className="landing-hero-redesign__proof-label">{pubText(proof.label)}</Box>
                      </Box>
                    </Box>
                  )))}
                </Box>

                <Box className="landing-hero-redesign__press-row">
                  <Box className="landing-hero-redesign__press-content">
                    <Box
                      component="a"
                      href={pubPath(newsImages[0].url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="landing-hero-redesign__single-news-icon"
                      aria-label={pubText("Read Fable article on NBC")}
                    >
                      <Box component="img" src="/download/nbc.svg" alt={pubText("NBC")} />
                    </Box>
                    <Box>
                      <Box className="landing-hero-redesign__featured-label">{pubText("Featured in")}</Box>
                      <Box className="landing-hero-redesign__featured-outlets">{pubText("NBC, ABC & CBS")}</Box>
                    </Box>
                  </Box>
                  <Box className="landing-hero-redesign__press-quote">
                    <Box component="span">"{pubText(heroMarketingContent.quote)}"</Box>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }} className="landing-hero-redesign__art">
                <Box className="landing-hero-redesign__art-stage">
                  <FadeVideo
                    videoSrc={routeConfig.path === '/' ? '/hero/hero-video.mp4' : undefined}
                    posterSrc={heroImage}
                    imageSrc={heroImage}
                    alt={pubText(routeConfig.heroImageAlt || 'Personalized Fable storybook scene')}
                    maxWidth="760px"
                    borderRadius="0"
                    horizontalFade={{ start: 30, end: 70 }}
                    verticalFade={{ start: 30, end: 70 }}
                  />
                </Box>
              </Grid>

              <Grid size={12} className="landing-hero-redesign__reviews">
                {pubText(heroReviewTiles.map((review) => (
                  <Box key={review.name} className="landing-hero-redesign__review-card">
                    <Box component="img" src={review.image} alt={pubText("")} aria-hidden="true" />
                    <Box>
                      <Box className="landing-hero-redesign__stars">★★★★★</Box>
                      <Typography component="p">"{pubText(review.review)}"</Typography>
                      <Box component="span">{pubText(review.name)}</Box>
                    </Box>
                  </Box>
                )))}
              </Grid>
            </Grid>
          </Box>
          {/* Hero Section with Background */}
          <Box sx={{
              display: 'none',
              position: 'relative',
              width: '100vw',
              marginInlineStart: 'calc(-50vw + 50%)',
              marginInlineEnd: 'calc(-50vw + 50%)',
              overflow: 'hidden',
          }}>
          {/* Hero Background Image - Using mask-image for seamless fade */}
          <Box
              component="img"
              src="/hero/Home-Main.jpeg"
              alt={pubText("Fable AI story generator hero background with illustrated characters")}
              sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: 0.15,
                  zIndex: 0,
                  pointerEvents: 'none',
                  maskImage: 'linear-gradient(to bottom, transparent 0%, transparent 20px, black 140px, black 70%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, transparent 20px, black 140px, black 70%, transparent 100%)',
              }}
          />

          {/* Magical Sparkle Particles - Pre-computed positions for stable rendering */}
          <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 0.5,
              pointerEvents: 'none',
              overflow: 'hidden',
          }}>
              {pubText([
                  { size: 3, left: 8, top: 25, delay: 0, duration: 5, color: '#FFF1A8' },
                  { size: 4, left: 15, top: 45, delay: 1.2, duration: 6, color: '#F5D061' },
                  { size: 2, left: 22, top: 35, delay: 2.5, duration: 4.5, color: '#FFD700' },
                  { size: 5, left: 30, top: 55, delay: 0.8, duration: 7, color: '#FFFACD' },
                  { size: 3, left: 38, top: 30, delay: 3.2, duration: 5.5, color: '#FFF1A8' },
                  { size: 4, left: 45, top: 65, delay: 1.5, duration: 6.5, color: '#F5D061' },
                  { size: 2, left: 52, top: 40, delay: 4.0, duration: 4, color: '#FFD700' },
                  { size: 5, left: 58, top: 50, delay: 0.3, duration: 7.5, color: '#FFFACD' },
                  { size: 3, left: 65, top: 28, delay: 2.0, duration: 5, color: '#FFF1A8' },
                  { size: 4, left: 72, top: 60, delay: 5.5, duration: 6, color: '#F5D061' },
                  { size: 2, left: 78, top: 38, delay: 1.8, duration: 4.5, color: '#FFD700' },
                  { size: 5, left: 85, top: 52, delay: 3.8, duration: 7, color: '#FFFACD' },
                  { size: 3, left: 92, top: 42, delay: 0.5, duration: 5.5, color: '#FFF1A8' },
                  { size: 4, left: 5, top: 70, delay: 6.2, duration: 6.5, color: '#F5D061' },
                  { size: 2, left: 35, top: 22, delay: 2.8, duration: 4, color: '#FFD700' },
                  { size: 5, left: 55, top: 75, delay: 4.5, duration: 7.5, color: '#FFFACD' },
                  { size: 3, left: 75, top: 32, delay: 1.0, duration: 5, color: '#FFF1A8' },
                  { size: 4, left: 88, top: 68, delay: 7.0, duration: 6, color: '#F5D061' },
                  { size: 2, left: 18, top: 58, delay: 3.5, duration: 4.5, color: '#FFD700' },
                  { size: 5, left: 48, top: 48, delay: 5.0, duration: 7, color: '#FFFACD' },
              ].map((sparkle, i) => (
                  <Box
                      key={i}
                      sx={{
                          position: 'absolute',
                          left: `${sparkle.left}%`,
                          top: `${sparkle.top}%`,
                          width: `${sparkle.size}px`,
                          height: `${sparkle.size}px`,
                          borderRadius: '50%',
                          background: sparkle.color,
                          boxShadow: `0 0 ${sparkle.size * 2}px ${sparkle.color}, 0 0 ${sparkle.size * 4}px ${sparkle.color}`,
                          opacity: 0,
                          animation: `sparkleFloat${i % 3} ${sparkle.duration}s ease-in-out ${sparkle.delay}s infinite`,
                          '@keyframes sparkleFloat0': {
                              '0%, 100%': {
                                  opacity: 0,
                                  transform: 'translateY(0) scale(0)'
                              },
                              '10%': {
                                  opacity: 0.8,
                                  transform: 'translateY(-10px) scale(1)'
                              },
                              '50%': {
                                  opacity: 1,
                                  transform: 'translateY(-50px) scale(1.2)'
                              },
                              '90%': {
                                  opacity: 0.3,
                                  transform: 'translateY(-100px) scale(0.5)'
                              },
                          },
                          '@keyframes sparkleFloat1': {
                              '0%, 100%': {
                                  opacity: 0,
                                  transform: 'translate(0, 0) scale(0)'
                              },
                              '15%': {
                                  opacity: 0.9,
                                  transform: 'translate(10px, -20px) scale(1)'
                              },
                              '50%': {
                                  opacity: 1,
                                  transform: 'translate(-10px, -60px) scale(1.1)'
                              },
                              '85%': {
                                  opacity: 0.2,
                                  transform: 'translate(5px, -120px) scale(0.4)'
                              },
                          },
                          '@keyframes sparkleFloat2': {
                              '0%, 100%': {
                                  opacity: 0,
                                  transform: 'translate(0, 0) scale(0) rotate(0deg)'
                              },
                              '20%': {
                                  opacity: 0.7,
                                  transform: 'translate(-15px, -15px) scale(1) rotate(90deg)'
                              },
                              '50%': {
                                  opacity: 1,
                                  transform: 'translate(10px, -40px) scale(1.3) rotate(180deg)'
                              },
                              '80%': {
                                  opacity: 0.4,
                                  transform: 'translate(-5px, -80px) scale(0.6) rotate(270deg)'
                              },
                          },
                      }}
                  />
              )))}
          </Box>

          <Box sx={{
              position: 'relative',
              zIndex: 1,
          }}>
          <Grid container spacing={4} columnSpacing={4} className="content" id="home" direction={{ xs: 'column-reverse', lg: 'row' }} style={{ paddingTop: showBanner ? '140px' : '70px', transition: 'padding-top 0.3s ease' }}>
              <Grid size={{ xs: 12, md: 12, lg: 6 }} className="imageContainer">
                  <Box sx={{
                      position: 'relative',
                      width: '100%',
                      minHeight: { xs: '450px', sm: '500px', md: '550px', lg: '450px' },
                      height: { xs: 'auto', sm: 'auto', md: 'auto', lg: '450px' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      opacity: 0,
                      animation: 'heroImageFadeIn 0.8s ease 300ms forwards',
                      '@keyframes heroImageFadeIn': {
                        from: { opacity: 0, transform: 'scale(0.9)' },
                        to: { opacity: 1, transform: 'scale(1)' },
                      },
                  }}>
                      {pubText(isHeroImageLoading && (
                          <Box sx={{
                              position: 'absolute',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                          }}>
                          </Box>
                      ))}
                      <img
                          src={heroImage}
                          alt={pubText(
                              routeConfig.heroImageAlt || "AI generated personalized bedtime stories with illustrations for kids"
                          )}
                          className="image image-slimmer"
                          loading="eager"
                          width="600"
                          height="800"
                          onLoad={handleHeroImageLoad}
                          onError={handleHeroImageError}
                          fetchPriority="high"
                          style={{
                              opacity: 1,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                          }}
                      />
                  </Box>
                  <Box sx={{
                      width: '100%',
                      mt: 2,
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'visible'
                  }}>
                      <Box sx={{
                          position: 'relative',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                      }}>
                          <Typography
                              variant="body1"
                              sx={{
                                  fontWeight: '600',
                                  fontSize: '1.5rem',
                                  background: heroTextGradients[heroIndex],
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text',
                                  position: 'relative',
                                  display: 'inline-block',
                              }}
                          >
                              {pubText(routeConfig.heroKicker || 'Your Story deserves to be told')}
                          </Typography>

                          {/* Free Story Badge */}
                          <Box
                              className="free-story-badge"
                              sx={{
                                  mt: 2,
                                  cursor: 'pointer',
                                  background: heroBadgeStyles[heroIndex].gradient,
                                  boxShadow: heroBadgeStyles[heroIndex].shadow,
                                  animation: 'none',
                                  '&:hover': {
                                      transform: 'scale(1.02)',
                                      transition: 'transform 0.2s ease'
                                  }
                              }}
                              onClick={useCallback(() => {
                                if (isAuthenticated) {
                                  navigateWithoutHash('/my-stories');
                                } else {
                                  navigateWithoutHash('/signin');
                                }
                              }, [navigateWithoutHash, isAuthenticated])}
                          >
                            {pubText(
                                routeConfig.heroBadgeText || 'Get your first free story in less than 60 seconds!'
                            )}
                          </Box>
                    {/* Mobile Buttons and Featured Section - Visible only on mobile */}
                    <Box sx={{ display: { xs: 'block', lg: 'none' }, width: '100%', mt: 0 }}>
                      <Grid container direction="row" spacing={2} className="buttonContainer" style={{marginTop: "10px"}} id="featured-in-mobile">
                          <Grid size={{ xs: 12, md: 12 }}>
                              <Box sx={{
                                  position: 'relative',
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                              }}>
                                  {pubText(isFeaturedImageLoading && (
                                      <Box sx={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          width: '90%',
                                          transform: 'translate(-50%, -50%)',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          gap: { xs: 1, sm: 1.25, md: 1.5 }
                                      }}>
                                      </Box>
                                  ))}
                              <Box sx={{ position: 'relative', display: 'inline-block', width: '90%', height: '100%' }}>
                                  <img
                                      className="image"
                                      src={featuredImage}
                                      alt={pubText("Fable AI story generator featured in major media outlets")}
                                      loading="lazy"
                                      width="800"
                                      height="400"
                                      onLoad={handleFeaturedImageLoad}
                                      onError={handleFeaturedImageError}
                                      style={{
                                          opacity: isFeaturedImageLoading ? 0 : 1,
                                          transition: 'opacity 0.2s ease-in-out',
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'contain'
                                      }}
                                  />
                                  {/* Left Zone - NBC */}
                                  <a
                                    href={pubPath(newsImages[0].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '33.33%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on NBC")}
                                  />
                                  {/* Middle Zone - ABC */}
                                  <a
                                    href={pubPath(newsImages[1].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: '33.33%',
                                      width: '33.33%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on ABC")}
                                  />
                                  {/* Right Zone - CBS */}
                                  <a
                                    href={pubPath(newsImages[2].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: '66.66%',
                                      width: '33.34%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on CBS")}
                                  />
                              </Box>
                              </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 8, md: 8, lg: 12 }} sx={{ display: 'none' }}>
                              {pubText(newsSlides)}
                          </Grid>
                      </Grid>
                    </Box>
                  </Box>
                  </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 12, lg: 6 }} className="textContainer" id="ai-story-generator">
                  <Box
                    sx={{
                      maxWidth: 600,
                      margin: '0 auto',
                      width: '100%',
                      textAlign: { xs: 'center', md: 'left' },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'center', md: 'flex-start' },
                    }}
                  >
                    <Typography variant="h2" className="title" sx={{
                      textAlign: { xs: 'center', sm: 'center', md: 'left' },
                      opacity: 0,
                      animation: 'heroTitleFadeIn 0.6s ease forwards',
                      '@keyframes heroTitleFadeIn': {
                        from: { opacity: 0, transform: 'translateY(20px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      background: 'linear-gradient(135deg, #ffffffff 20%, #ffffffff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: 'none',
                      filter: 'drop-shadow(0 0 1px rgba(245, 208, 97, 0.3))',
                    }}>
                      {pubText(routeConfig.title)}
                    </Typography>
                    {pubText(routeConfig.subtitle && (
                      <Typography variant="h2" sx={{
                        textAlign: { xs: 'center', sm: 'center', md: 'left' },
                        fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.85)',
                        mt: -0.5,
                        mb: 1.5,
                        opacity: 0,
                        animation: 'heroSubtitleFadeIn 0.6s ease 50ms forwards',
                        '@keyframes heroSubtitleFadeIn': {
                          from: { opacity: 0, transform: 'translateY(20px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                      }}>
                        {pubText(routeConfig.subtitle)}
                      </Typography>
                    ))}
                    <Typography variant="body1" className="description" sx={{
                      textAlign: { xs: 'center', sm: 'center', md: 'left' },
                      width: '100%',
                      opacity: 0,
                      animation: 'heroDescFadeIn 0.6s ease 100ms forwards',
                      '@keyframes heroDescFadeIn': {
                        from: { opacity: 0, transform: 'translateY(20px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}>
                      {pubText(routeConfig.description)}
                    </Typography>

                    <Box sx={{
                      width: '100%',
                      opacity: 0,
                      animation: 'heroButtonsFadeIn 0.6s ease 200ms forwards',
                      '@keyframes heroButtonsFadeIn': {
                        from: { opacity: 0, transform: 'translateY(20px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}>
                    <Grid
                      className="buttonContainer"
                      sx={{
                        mt: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                      id="download"
                    >
                      <Grid size={{ xs: 12, sm: 8, md: 6 }} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <OneLinkDownloadButton
                          label={pubText(routeConfig.ctaLabel || "Download Fable")}
                          fullWidth
                          onClick={() => handleDownloadClick('hero')}
                          sx={{
                            background: heroDownloadButtonStyles[heroIndex].background,
                            boxShadow: heroDownloadButtonStyles[heroIndex].shadow,
                            color: heroDownloadButtonStyles[heroIndex].color,
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.55)',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              background: heroDownloadButtonStyles[heroIndex].hoverBackground,
                              boxShadow: heroDownloadButtonStyles[heroIndex].hoverShadow,
                              color: heroDownloadButtonStyles[heroIndex].color,
                              textShadow: '0 1px 2px rgba(0, 0, 0, 0.55)',
                            },
                            '&:active': {
                              transform: 'translateY(1px)',
                              boxShadow: heroDownloadButtonStyles[heroIndex].shadow,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                    </Box>
                  </Box>
                  <Box sx={{ display: { xs: 'none', lg: 'block' }, width: '100%' }}>
                  <Grid container direction="row" spacing={2} className="buttonContainer section-extra" id="featured-in">
                      <Grid size={{ xs: 12, md: 12 }}>
                          <Box sx={{
                              position: 'relative',
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                          }}>
                              {pubText(isFeaturedImageLoading && (
                                  <Box sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      maxWidth: '90%',
                                      transform: 'translate(-50%, -50%)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: { xs: 1, sm: 1.25, md: 1.5 }
                                  }}>
                                  </Box>
                              ))}
                              <Box sx={{ position: 'relative', display: 'inline-block', width: '90%', height: '100%' }}>
                                  <img
                                      className="image"
                                      src={featuredImage}
                                      alt={pubText("Fable AI story generator featured in major media outlets")}
                                      loading="lazy"
                                      width="800"
                                      height="400"
                                      onLoad={handleFeaturedImageLoad}
                                      onError={handleFeaturedImageError}
                                      style={{
                                          opacity: isFeaturedImageLoading ? 0 : 1,
                                          transition: 'opacity 0.2s ease-in-out',
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'contain'
                                      }}
                                  />
                                  {/* Left Zone - NBC */}
                                  <a
                                    href={pubPath(newsImages[0].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '33.33%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on NBC")}
                                  />
                                  {/* Middle Zone - ABC */}
                                  <a
                                    href={pubPath(newsImages[1].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: '33.33%',
                                      width: '33.33%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on ABC")}
                                  />
                                  {/* Right Zone - CBS */}
                                  <a
                                    href={pubPath(newsImages[2].url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: '66.66%',
                                      width: '33.34%',
                                      height: '100%',
                                      zIndex: 10,
                                      cursor: 'pointer'
                                    }}
                                    aria-label={pubText("Read article on CBS")}
                                  />
                              </Box>
                          </Box>
                      </Grid>
                  </Grid>
                  </Box>
              </Grid>
          </Grid>
          </Box>
          </Box>
          {/* Reviews Section */}
          <Grid container spacing={4} className="section" id="what-our-users-say" sx={{
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              padding: 0,
          }}>
              {/* Title - constrained width */}
              <Grid size={12} className={"textContainer"} sx={{
                  maxWidth: '1200px',
                  margin: '0 auto',
                  padding: { xs: '0 16px', sm: '0 24px', md: '0 32px' },
              }}>
                  <HomeSectionDivider />
                  <Typography  variant="body2"
                              sx={{
                                  fontWeight: 'bold',
                                  fontSize: '1.8rem',
                                  background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700, #FFA500, #FFD700)',
                                  backgroundSize: '200% auto',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text',
                                  textShadow: 'none',
                                  filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.6))',
                                  mt: 0,
                                  animation: 'shimmer 3s linear infinite, pulse 2s ease-in-out infinite',
                                  '@keyframes shimmer': {
                                      '0%': {
                                          backgroundPosition: '0% center'
                                      },
                                      '100%': {
                                          backgroundPosition: '200% center'
                                      }
                                  },
                                  '@keyframes pulse': {
                                      '0%, 100%': {
                                          transform: 'scale(1)'
                                      },
                                      '50%': {
                                          transform: 'scale(1.05)'
                                      }
                                  }
                              }}>{pubText(" Customers Love Fable! ")}</Typography>
                  <Typography
                      variant="body2"
                      sx={{
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)',
                          backgroundSize: '200% auto',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          mt: 1,
                          mb: 1,
                      }}
                  >{pubText(" Stories, imagination, and experiences shared by Fable customers ")}</Typography>
              </Grid>

              {/* Reviews Carousel - full width */}
              <Grid size={12} sx={{ width: '100vw', maxWidth: '100vw', marginInlineStart: 'calc(-50vw + 50%)', padding: 0 }}>
                  <Box sx={{
                      mt: 3,
                      position: 'relative',
                      width: '100%',
                      overflow: 'hidden',
                      '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '150px',
                          height: '100%',
                          background: 'linear-gradient(90deg, rgba(23, 23, 36, 1) 0%, rgba(23, 23, 36, 0) 100%)',
                          zIndex: 2,
                          pointerEvents: 'none'
                      },
                      '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '150px',
                          height: '100%',
                          background: 'linear-gradient(270deg, rgba(23, 23, 36, 1) 0%, rgba(23, 23, 36, 0) 100%)',
                          zIndex: 2,
                          pointerEvents: 'none'
                      }
                  }}>
                      <Box sx={{
                          display: 'flex',
                          gap: 3,
                          animation: 'scroll 45s linear infinite',
                          '@keyframes scroll': {
                              '0%': {
                                  transform: 'translateX(0)'
                              },
                              '100%': {
                                  transform: 'translateX(-50%)'
                              }
                          }
                      }}>
                          {pubText([...reviews, ...reviews, ...reviews, ...reviews].map((review, index) => (
                          <Box
                              key={index}
                              sx={{
                                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                                  backdropFilter: 'blur(10px)',
                                  borderRadius: '16px',
                                  padding: '20px',
                                  minWidth: { xs: '300px', sm: '350px', md: '400px' },
                                  maxWidth: { xs: '300px', sm: '350px', md: '400px' },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  flexShrink: 0
                              }}
                          >
                              <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 1
                              }}>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {pubText([...Array(5)].map((_, i) => (
                                          <Box
                                              key={i}
                                              component="span"
                                              sx={{
                                                  color: i < review.rating ? '#FFD700' : 'rgba(255, 255, 255, 0.2)',
                                                  fontSize: '1.1rem',
                                                  animation: i < review.rating ? 'starShine 2s infinite' : 'none',
                                                  animationDelay: `${i * 0.2}s`,
                                                  '@keyframes starShine': {
                                                      '0%, 100%': {
                                                          opacity: 1,
                                                          filter: 'brightness(1) drop-shadow(0 0 2px rgba(255, 215, 0, 0.5))'
                                                      },
                                                      '50%': {
                                                          opacity: 1,
                                                          filter: 'brightness(1.8) drop-shadow(0 0 12px rgba(255, 215, 0, 0.9))'
                                                      }
                                                  }
                                              }}
                                          >
                                              ★
                                          </Box>
                                      )))}
                                  </Box>
                              </Box>
                              <Typography variant="body2" sx={{
                                  fontStyle: 'italic',
                                  color: '#fff',
                                  fontSize: '0.95rem',
                                  textAlign: "start",
                                  lineHeight: 1.4,
                                  opacity: 0.9,
                                  mt: 1,
                                  flex: 1
                              }}>
                                  "{pubText(review.review)}"
                              </Typography>
                              <Typography variant="subtitle2" sx={{
                                  fontWeight: 'bold',
                                  color: '#D8A363',
                                  fontSize: '0.9rem',
                                  textAlign: "start",
                              }}>
                                  {pubText(review.name)}
                              </Typography>
                          </Box>
                          )))}
                      </Box>
                  </Box>
              </Grid>
          </Grid>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 6,
                  marginTop: 6
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },

                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swooshReverse}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* YouTube Video Section */}
          <Grid
            container
            spacing={4}
            className="content section"
            id="fable-demo-video"
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              mx: 'auto',
              overflow: 'hidden',
            }}
          >
              <Grid size={12} className={"textContainer textContainerRelative"} style={{marginTop: "0px"}}>
                  <FadeInSection sx={{ width: '100%' }}>
                  <HomeSectionDivider />
                  <Typography variant="h2" sx={{
                      color: '#fff',
                      fontFamily: 'var(--fable-heading-font)',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>
                      <span style={{ background: 'linear-gradient(90deg, #FFB347, #FF8C42, #E879F9, #C084FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Watch the Magic unfold...")}</span>
                  </Typography>

                  <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                      " Fable is an AI story generator that blends classic storytelling with cutting-edge generative AI technology. Our app leverages advanced text, image and audio models to help you create personalized fairy tales, bedtime stories, and adventures that educate, entertain, and inspire. "
                  )}</Typography>
                  <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 4,
                      mb: 0,
                      px: { xs: 0, sm: 3, md: 0 },
                      width: '100%',
                      maxWidth: '100%',
                      mx: 'auto',
                      boxSizing: 'border-box'
                  }}>
                      <Box
                          data-testid="homepage-demo-video-player"
                          role={videoPlaying ? undefined : 'button'}
                          aria-label={pubText(videoPlaying ? undefined : 'Play Turn Your Idea Into a Living Masterpiece')}
                          tabIndex={videoPlaying ? -1 : 0}
                          onClick={() => {
                              if (!videoPlaying) setVideoPlaying(true);
                          }}
                          onKeyDown={(event) => {
                              if (!videoPlaying && (event.key === 'Enter' || event.key === ' ')) {
                                  event.preventDefault();
                                  setVideoPlaying(true);
                              }
                          }}
                          sx={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          aspectRatio: '16 / 9',
                          borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                          overflow: 'hidden',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                          border: '2px solid rgba(216, 163, 99, 0.3)',
                          backgroundColor: '#000',
                          cursor: videoPlaying ? 'default' : 'pointer',
                          '&:hover': videoPlaying ? undefined : {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                              border: '2px solid rgba(216, 163, 99, 0.5)',
                          },
                          transition: 'all 0.3s ease'
                      }}>
                          {pubText(videoPlaying ? (
                              <iframe
                                  src="https://www.youtube.com/embed/HGEjSe1p_rs?autoplay=1&rel=0&modestbranding=1&playsinline=1"
                                  title={pubText("Turn Your Idea Into a Living Masterpiece")}
                                  style={{
                                      position: 'absolute',
                                      inset: 0,
                                      width: '100%',
                                      height: '100%',
                                      border: 'none',
                                      borderRadius: 'inherit',
                                      display: 'block',
                                      backgroundColor: '#000',
                                  }}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                  allowFullScreen
                              />
                          ) : (
                              <>
                                  <img
                                      src="/fable-living-masterpiece-video-thumbnail.jpg"
                                      alt={pubText("Turn Your Idea Into a Living Masterpiece")}
                                      loading="lazy"
                                      style={{
                                          position: 'absolute',
                                          inset: 0,
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          objectPosition: 'center',
                                          borderRadius: 'inherit',
                                          display: 'block',
                                      }}
                                  />
                                  {/* Play button overlay */}
                                  <Box sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: 68,
                                      height: 48,
                                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                      borderRadius: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'background-color 0.2s ease',
                                      '&:hover': { backgroundColor: '#ff0000' }
                                  }}>
                                      <Box sx={{
                                          width: 0,
                                          height: 0,
                                          borderStyle: 'solid',
                                          borderWidth: '10px 0 10px 18px',
                                          borderColor: 'transparent transparent transparent #fff',
                                          marginInlineStart: '4px'
                                      }} />
                                  </Box>
                              </>
                          ))}
                      </Box>
                  </Box>
                  {/* My Stories Button below video */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleMyStoriesClick}
                      endIcon={<KeyboardArrowRight sx={{ fontSize: 24, color: '#fff'}} />}
                      className="my-stories-button"
                      sx={{
                        alignSelf: 'center' ,
                        background: 'linear-gradient(135deg, #5B8BC7, #4A7BB8)',
                        color: '#fff',
                        fontWeight: 'bold',
                        borderRadius: '32px',
                        padding: '12px 32px',
                        fontSize: '1.05rem',
                        lineHeight: 1.2,
                        boxShadow: '0 8px 32px rgba(91, 139, 199, 0.15)',
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4A7BB8, #5B8BC7)',
                        },
                      }}
                    >{pubText(" My Stories ")}</Button>
                  </Box>
                  </FadeInSection>
              </Grid>
          </Grid>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swoosh}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          <StoryCreationWays />
          <Grid size={12} className="imageContainer section" style={{marginTop: "0px", marginBottom: "20px"}}>
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swooshReverse}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* Printed Books Section */}
          <FadeInSection>
            <Grid container spacing={2} className="content" id="printed-books">
              <Grid size={12} className="textContainer">
                <HomeSectionDivider spaceBefore />
                <Typography variant="h2" sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                  mb: 2,
                  textAlign: 'center',
                }}>{pubRich(" Make it a __SLOT_0__ Worth Keeping ", [
                        <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD580, #FFBD59, #FFA94D)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Book")}</Box>
                    ])}</Typography>
                <Typography variant="body1" className="description centered" sx={{ fontWeight: 500, maxWidth: 980, mx: 'auto' }}>{pubText(
                    " Turn any story into a custom printed book, from children’s adventures and anime epics to romance, fantasy and everything in between. "
                )}</Typography>
              </Grid>

              <Grid size={12} sx={{ mt: 0, mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src="/images/books/custom-books-hero-bookshelf-720.webp"
                  srcSet="/images/books/custom-books-hero-bookshelf-720.webp 720w, /images/books/custom-books-hero-bookshelf.webp 1088w"
                  sizes="(max-width: 900px) 100vw, 1088px"
                  loading="lazy"
                  alt={pubText(
                      "Custom anime, children's, romance, fantasy, hardcover and coil-bound books displayed together"
                  )}
                  sx={{
                    width: '100%',
                    maxWidth: 1088,
                    display: 'block',
                    WebkitMaskImage: SOFT_MEDIA_EDGE_MASK,
                    WebkitMaskComposite: 'source-in',
                    maskImage: SOFT_MEDIA_EDGE_MASK,
                    maskComposite: 'intersect',
                  }}
                />
              </Grid>

              <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  onClick={() => navigate(pubPath('/custom-books'))}
                  endIcon={<KeyboardArrowRight />}
                  sx={{
                    background: 'linear-gradient(135deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)',
                    color: '#1a1625',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    minHeight: 46,
                    px: 3,
                    py: 1.1,
                    borderRadius: '24px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(216, 163, 99, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8A54C 0%, #FF9F32 50%, #FFB347 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(216, 163, 99, 0.5)',
                    },
                  }}
                >{pubText(" Explore custom books ")}</Button>
              </Grid>
            </Grid>
          </FadeInSection>
          {/* Make it a Musical Section */}
          <FadeInSection>
          <Grid container spacing={2} className="content section-extra" id="make-it-musical">
              {/* Title */}
              <Grid size={12} className={"textContainer section"}>
                    <HomeSectionDivider />
                    <Typography variant="h2" sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>{pubRich(" Make it a __SLOT_0__ ", [
                            <span style={{ background: 'linear-gradient(135deg, #FFD580, #FFBD59, #FFA94D)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Musical")}</span>
                        ])}</Typography>
                    <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                        " Transform your stories into magical musical adventures with custom songs and melodies. Music creates emotional connections and makes stories truly memorable for children. "
                    )}</Typography>
              </Grid>

              {/* Video Carousel + Dots */}
              <Grid size={12} style={{marginTop: "0px", marginBottom: "16px"}}>
                  <MusicalCarousel onMusicalChange={setCurrentMusicalIndex} />
              </Grid>

              {/* Music Player - Right below carousel */}
              <Grid size={12} style={{marginTop: "12px"}}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <MusicPlayer
                          key={currentTrack.src}
                          src={currentTrack.src}
                          title={pubText(currentTrack.title)}
                          subtitle={pubText(currentTrack.subtitle)}
                      />
                  </Box>
              </Grid>

              {/* Description Text */}
              <Grid size={12} style={{marginTop: "24px"}}>
                  <Typography variant="body1" className={"description centered"} style={{fontWeight: "normal", maxWidth: "900px", margin: "0 auto"}}>{pubText(
                      " Create a range of musical songs from gentle lullabies to upbeat adventure themes. Each song is uniquely composed to match your story's mood and characters, featuring original melodies, harmonies, and even voices singing along. We understand the power of music and how it can enhance a story, so we made it easy for you to create your very own musicals! "
                  )}</Typography>
              </Grid>

              {/* CTA Buttons */}
              <Grid size={12} style={{marginTop: "24px"}}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                          variant="outlined"
                          onClick={() => navigate(pubPath('/musicals'))}
                          sx={{
                              borderColor: '#D8A363',
                              color: '#D8A363',
                              fontWeight: 600,
                              padding: '10px 28px',
                              borderRadius: '24px',
                              textTransform: 'none',
                              fontSize: '1rem',
                              minWidth: '160px',
                              border: '2px solid #D8A363',
                              boxShadow: '0 4px 12px rgba(216, 163, 99, 0.3)',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                  borderColor: '#E8B373',
                                  background: 'rgba(216, 163, 99, 0.1)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 20px rgba(216, 163, 99, 0.4)',
                              },
                              '&:active': {
                                  transform: 'translateY(1px)',
                                  boxShadow: '0 2px 6px rgba(216, 163, 99, 0.3)',
                              },
                          }}
                      >{pubText(" View All Musicals ")}</Button>
                      <Button
                          variant="contained"
                          onClick={() => isAuthenticated ? navigate(pubPath('/my-stories')) : navigate(pubPath('/signin'))}
                          sx={{
                              background: 'linear-gradient(135deg, #D8A363, #E8B373)',
                              color: '#1a1a1a',
                              fontWeight: 600,
                              padding: '10px 28px',
                              borderRadius: '24px',
                              textTransform: 'none',
                              fontSize: '1rem',
                              minWidth: '160px',
                              boxShadow: '0 8px 24px rgba(216, 163, 99, 0.4)',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                  background: 'linear-gradient(135deg, #E8B373, #F8C383)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 12px 32px rgba(216, 163, 99, 0.5)',
                              },
                              '&:active': {
                                  transform: 'translateY(1px)',
                                  boxShadow: '0 4px 8px rgba(216, 163, 99, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.4), 0 1px 2px rgba(216, 163, 99, 0.3)',
                              },
                              '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '50%',
                                  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0))',
                                  borderRadius: '24px 24px 0 0',
                              },
                          }}
                      >{pubText(" Unlock Fable ")}</Button>
                  </Box>
              </Grid>
          </Grid>
          </FadeInSection>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 4,
                  marginTop: 4
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swoosh}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          <HomeVoiceShowcase />
          <Grid size={12} className="imageContainer" style={{marginBottom: "32px"}}>
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swooshReverse}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* Tutorial Slides */}
          <FadeInSection>
          <Grid container spacing={4} className="content section-extra" id="let-fable-create-story">
              <Grid size={12} className={"textContainer section"}>
                    <HomeSectionDivider />
                    <Typography variant="h2" sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>{pubRich(" Let __SLOT_0__ Create Your Story ", [
                            <span style={{ background: 'linear-gradient(135deg, #93C5FD, #60A5FA, #7DD3FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Fable AI")}</span>
                        ])}</Typography>
                    <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                        " Create engaging bedtime stories and short stories for kids with our AI storytelling generator. Just a few words and you're ready to go! "
                    )}</Typography>
                  <Grid size={12} className="slideContainer" style={{marginTop: "40px", position: "relative"}}>
                      <Swiper
                          ref={storySwiperRef}
                          effect={swiperEffect}
                          grabCursor={true}
                          modules={[Pagination]}
                          slidesPerView={1}
                          initialSlide={4}
                          centeredSlides={true}
                          spaceBetween={60}
                          style={{ overflow: "visible", maxWidth: "300px", cursor: "grab" }}
                          pagination={{
                              clickable: true,
                              el: '.story-swiper-pagination',
                              bulletClass: 'swiper-pagination-bullet',
                              bulletActiveClass: 'swiper-pagination-bullet-active',
                          }}
                          key={"stories"}
                      >
                          {pubText(storySlides)}
                      </Swiper>
                      <Box className="story-swiper-pagination" sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 4,
                          '& .swiper-pagination-bullet': {
                              width: { xs: '6px', sm: '8px', md: '10px' },
                              height: { xs: '6px', sm: '8px', md: '10px' },
                              backgroundColor: 'rgba(216, 163, 99, 0.3)',
                              opacity: 1,
                              margin: { xs: '0 2px', sm: '0 3px', md: '0 4px' },
                              transition: 'all 0.3s ease'
                          },
                          '& .swiper-pagination-bullet-active': {
                              backgroundColor: 'rgba(216, 163, 99, 1)',
                              transform: { xs: 'scale(1.1)', sm: 'scale(1.15)', md: 'scale(1.2)' }
                          }
                      }}></Box>
                      <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: { xs: .75, sm: 1 },
                          mb: 1,
                          mt: 2.5
                      }}>
                          <Button
                              onClick={useCallback(() => {
                                  handlePrevClick(storySwiperRef);
                                  trackSwiperInteraction('story', 'click', undefined, 'previous_button');
                              }, [storySwiperRef])}
                              startIcon={<KeyboardArrowLeft sx={{ fontSize: '20px !important' }} />}
                              sx={homeCarouselNavButtonSx}
                          >{pubText(" Previous ")}</Button>
                          <Button
                              onClick={useCallback(() => {
                                  handleNextClick(storySwiperRef);
                                  trackSwiperInteraction('story', 'click', undefined, 'next_button');
                              }, [storySwiperRef])}
                              endIcon={<KeyboardArrowRight sx={{ fontSize: '20px !important' }} />}
                              sx={homeCarouselNavButtonSx}
                          >{pubText(" Next ")}</Button>
                      </Box>
                  </Grid>
              </Grid>
          </Grid>
          </FadeInSection>
          <Grid container spacing={4} className="content section">
               <Grid size={12} className={"textContainer textContainerRelative"}>
                  <Grid container direction="row" spacing={2} className="buttonContainer">
                      <Grid size={{ xs: 12, md: 5, lg: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <OneLinkDownloadButton
                              label={pubText("Try Fable")}
                              fullWidth
                              onClick={() => handleDownloadClick('tutorial')}
                          />
                      </Grid>
                  </Grid>
              </Grid>
          </Grid>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 4,
                  marginTop: 4
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swoosh}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          <FadeInSection>
          <Grid container spacing={4} className="content section-extra" id="story-styles">
              <Grid size={12} className={"textContainer section"}>
                    <HomeSectionDivider />
                    <Typography variant="h2" sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>{pubRich(" Your Story In __SLOT_0__ ", [
                            <span style={{ background: 'linear-gradient(135deg, #FEF08A, #FDE047, #FACC15)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Any Style")}</span>
                        ])}</Typography>
                    <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                        " From 3D cartoons to watercolor paintings, create your stories in any visual style you can imagine. Each style brings its own unique vibe to your storytelling experience. "
                    )}</Typography>
              </Grid>
          </Grid>
          {/* Style Showcase Section */}
          <Grid container spacing={4} className="content">
              <Grid size={12} className={"textContainer"}>
                  <Grid size={12} className="slideContainer" style={{marginTop: "32px", position: "relative"}}>
                      <Swiper
                          ref={styleSwiperRef}
                          effect="coverflow"
                          grabCursor={true}
                          modules={[EffectCoverflow, Pagination]}
                          slidesPerView={matchesXl ? 3 : (matchesLg ? 3 : (matchesMd ? 3 : 3))}
                          initialSlide={0}
                          centeredSlides={true}
                          spaceBetween={10}
                          className="voice-carousel style-carousel"
                          coverflowEffect={{
                              rotate: 0,
                              stretch: 0,
                              depth: matchesXl ? 200 : (matchesLg ? 200 : (matchesMd ? 200 : 450)),
                              modifier: 2.5,
                              slideShadows: false,
                          }}
                          pagination={{
                              clickable: true,
                              el: '.style-swiper-pagination',
                              bulletClass: 'swiper-pagination-bullet',
                              bulletActiveClass: 'swiper-pagination-bullet-active',
                          }}
                          style={{
                              overflow: 'visible',
                              cursor: "grab",
                              background: 'transparent',
                              marginInlineEnd: "36px"
                          }}
                          key={"styles"}
                      >
                          {pubText(styleSlides)}
                      </Swiper>
                      <Box className="style-swiper-pagination" sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 4,
                          '& .swiper-pagination-bullet': {
                              width: { xs: '6px', sm: '8px', md: '10px' },
                              height: { xs: '6px', sm: '8px', md: '10px' },
                              backgroundColor: 'rgba(216, 163, 99, 0.3)',
                              opacity: 1,
                              margin: { xs: '0 2px', sm: '0 3px', md: '0 4px' },
                              transition: 'all 0.3s ease'
                          },
                          '& .swiper-pagination-bullet-active': {
                              backgroundColor: 'rgba(216, 163, 99, 1)',
                              transform: { xs: 'scale(1.1)', sm: 'scale(1.15)', md: 'scale(1.2)' }
                          }
                      }}></Box>
                      <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: { xs: .75, sm: 1 },
                          mb: 1,
                          mt: 2.5
                      }}>
                          <Button
                              onClick={useCallback(() => {
                                  handlePrevClick(styleSwiperRef);
                                  trackSwiperInteraction('story', 'click', undefined, 'style_previous_button');
                              }, [styleSwiperRef])}
                              startIcon={<KeyboardArrowLeft sx={{ fontSize: '20px !important' }} />}
                              sx={homeCarouselNavButtonSx}
                          >{pubText(" Previous ")}</Button>
                          <Button
                              onClick={useCallback(() => {
                                  handleNextClick(styleSwiperRef);
                                  trackSwiperInteraction('story', 'click', undefined, 'style_next_button');
                              }, [styleSwiperRef])}
                              endIcon={<KeyboardArrowRight sx={{ fontSize: '20px !important' }} />}
                              sx={homeCarouselNavButtonSx}
                          >{pubText(" Next ")}</Button>
                      </Box>
                  </Grid>

                  {/* View All Styles and Create Story Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4, mb: 2, px: { xs: 4, sm: 0 }, py: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate(pubPath('/styles'))}
                      sx={{
                        whiteSpace: 'nowrap',
                        padding: '12px 32px',
                        borderRadius: '32px',
                        background: 'linear-gradient(135deg, #D8A363, #E8B373)',
                        boxShadow: '0 8px 24px rgba(216, 163, 99, 0.4)',
                        border: 'none',
                        color: '#000',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        minWidth: '200px',
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(216, 163, 99, 0.5)',
                          background: 'linear-gradient(135deg, #E8B373, #D8A363)',
                        },
                        '&:active': {
                          transform: 'translateY(1px)',
                          boxShadow: '0 4px 8px rgba(216, 163, 99, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.4), 0 1px 2px rgba(216, 163, 99, 0.3)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '50%',
                          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0))',
                          borderRadius: '32px 32px 0 0',
                        }
                      }}
                    >{pubText(" View All Styles ")}</Button>
                    <Button
                      variant="contained"
                      onClick={handleMyStoriesClick}
                      endIcon={<KeyboardArrowRight sx={{ height: 24, width: 24, fontWeight: 'bold', color: '#fff'}} />}
                      className="my-stories-button"
                      sx={{
                        whiteSpace: 'nowrap',
                        padding: '12px 32px',
                        borderRadius: '32px',
                        background: 'linear-gradient(135deg, #5B8BC7, #4A7BB8)',
                        boxShadow: '0 8px 24px rgba(91, 139, 199, 0.4)',
                        border: 'none',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.05rem',
                        lineHeight: 1.2,
                        minWidth: '200px',
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(91, 139, 199, 0.5)',
                          background: 'linear-gradient(135deg, #4A7BB8, #5B8BC7)',
                        },
                        '&:active': {
                          transform: 'translateY(1px)',
                          boxShadow: '0 4px 8px rgba(91, 139, 199, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.4), 0 1px 2px rgba(91, 139, 199, 0.3)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-50%',
                          left: '-50%',
                          width: '200%',
                          height: '200%',
                          background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)',
                          animation: 'shimmer 3s infinite',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '50%',
                          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))',
                          borderRadius: '32px 32px 0 0',
                        },
                        '@keyframes shimmer': {
                          '0%': {
                            transform: 'translateX(-100%) translateY(-100%) rotate(45deg)',
                          },
                          '100%': {
                            transform: 'translateX(100%) translateY(100%) rotate(45deg)',
                          },
                        },
                      }}
                    >{pubText(" Unlock Fable ")}</Button>
                  </Box>
              </Grid>
          </Grid>
          </FadeInSection>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 4,
                  marginTop: 4
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swooshReverse}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* Globe Section - Fable's Global Reach */}
          <FadeInSection>
          <Grid container spacing={4} className="content section-extra" id="languages" sx={{ mt: 8, mb: 8 }}>
              <Grid size={12} className={"textContainer section"}>
                    <HomeSectionDivider />
                    <Typography variant="h2" sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>{pubRich(" Stories __SLOT_0__ ", [
                            <span style={{ background: 'linear-gradient(135deg, #99F6E4, #5EEAD4, #2DD4BF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Around the World")}</span>
                        ])}</Typography>
                    <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                        " Join families across the globe who are creating their stories with Fable in 24 different languages. From New York to Tokyo, from London to Sydney, parents and children worldwide are discovering the joy of personalized storytelling in their native language. "
                    )}</Typography>
              </Grid>
              <Grid size={12} sx={{
                  padding: { xs: 2, sm: 3, md: 4 },
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #2d1b69 50%, #1a1b4b 75%, #0f172a 100%)',
                  borderRadius: '20px',
                  boxShadow: `
                      0 25px 50px rgba(139, 92, 246, 0.15),
                      0 12px 25px rgba(59, 130, 246, 0.1),
                      0 6px 12px rgba(249, 115, 22, 0.08),
                      inset 0 1px 0 rgba(136, 207, 255, 0.2)
                  `,
                  border: '1px solid rgba(136, 207, 255, 0.2)',
              }}>
                  <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ width: '100%' }}>
                      {pubText(languageData.map((language, index) => (
                          <Grid
                              size={{ xs: 6, sm: 4, md: 3, lg: 2, xl: 2 }}
                              key={index}
                              sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                              }}
                          >
                              <Box
                                  onClick={() => navigate(pubPath(`/languages/${language.id}`))}
                                  sx={{
                                      position: 'relative',
                                      width: '100%',
                                      aspectRatio: '1/1',
                                      borderRadius: '12px',
                                      overflow: 'hidden',
                                      backgroundImage: `url(${language.image})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                                      '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 8px 25px rgba(216, 163, 99, 0.4)',
                                      },
                                      '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)',
                                          zIndex: 1,
                                          transition: 'all 0.3s ease',
                                      },
                                      '&:hover::before': {
                                          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.7) 100%)',
                                      },
                                  }}
                              >
                                  <Box
                                      sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          zIndex: 2,
                                          padding: { xs: 1, sm: 1.5 },
                                          textAlign: 'center',
                                      }}
                                  >
                                      <Typography
                                          variant="h6"
                                          sx={{
                                              color: 'white',
                                              fontWeight: 'bold',
                                              fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1rem' },
                                              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                                              lineHeight: 1.2,
                                          }}
                                      >
                                          {pubText(language.name)}
                                      </Typography>
                                  </Box>
                              </Box>
                          </Grid>
                      )))}
                  </Grid>
              </Grid>
          </Grid>
          </FadeInSection>
          {/* Download button under Languages Section */}
          <Grid container direction="row" spacing={2} className="buttonContainer" sx={{ mt: 4, mb: 2, justifyContent: 'center' }}>
              <Grid size={{ xs: 12, md: 5, lg: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                <OneLinkDownloadButton
                  label={pubText("Download Fable")}
                  fullWidth
                  onClick={() => handleDownloadClick('languages')}
                />
              </Grid>
          </Grid>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 4
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swooshReverse}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* Morals Section - Teach Valuable Life Lessons */}
          <FadeInSection>
          <Grid container spacing={4} className="content section-extra" id="teach-morals" sx={{ mt: 8, mb: 8 }}>
              <Grid size={12} className={"textContainer section"}>
                    <HomeSectionDivider />
                    <Typography variant="h2" sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '3rem', sm: '3rem', md: '4rem', lg: '5rem' },
                      mb: 2,
                      textAlign: 'center',
                  }}>{pubRich(" Teach __SLOT_0__ ", [
                            <span style={{ background: 'linear-gradient(135deg, #FECDD3, #FDA4AF, #FB7185)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pubText("Valuable Life Lessons")}</span>
                        ])}</Typography>
                    <Typography variant="body1" className={"description centered"} style={{fontWeight: "500"}}>{pubText(
                        " Every story is an opportunity to teach important values. Fable weaves meaningful life lessons into engaging adventures, helping children learn kindness, courage, honesty, and more through personalized storytelling. "
                    )}</Typography>
              </Grid>
              <Grid size={12} sx={{
                  position: 'relative'
              }}>
                  <Swiper
                      style={{
                          '--swiper-navigation-color': '#fff',
                          '--swiper-pagination-color': '#fff',
                          width: '100%',
                          aspectRatio: '1/1',
                          maxWidth: 'min(600px, 85vw)',
                          margin: '0 auto',
                          borderRadius: '20px',
                          overflow: 'hidden',
                          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
                      } as React.CSSProperties}
                      speed={600}
                      parallax={true}
                      pagination={{
                          clickable: true,
                      }}
                      navigation={true}
                      modules={[Parallax, Pagination, Navigation, Autoplay]}
                      className="morals-swiper morals-swiper-small-nav"
                      loop={true}
                      autoplay={{
                          delay: 5000,
                          disableOnInteraction: false,
                      }}
                  >
                      {pubText(moralsData.map((moral, index) => (
                          <SwiperSlide key={moral.id}>
                              <Box
                                  sx={{
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      position: 'relative',
                                      cursor: 'pointer',
                                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.6)), url(${moral.image})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                      transition: 'all 0.3s ease',
                                      textAlign: 'center'
                                  }}
                                  onClick={() => navigateWithoutHash(`/morals/${moral.id}`)}
                              >
                                  <Typography
                                      variant="h3"
                                      data-swiper-parallax="-300"
                                      sx={{
                                          paddingInlineStart: { xs: '40px', md: '40px' },
                                          paddingInlineEnd: { xs: '40px', md: '40px' },
                                          color: '#fff',
                                          fontWeight: 'bold',
                                          fontSize: { xs: '2rem', md: '3rem' },
                                          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                                          mb: 2
                                      }}
                                  >
                                      {pubText(moral.name)}
                                  </Typography>
                                  <Typography
                                      variant="h6"
                                      data-swiper-parallax="-200"
                                      sx={{
                                          paddingInlineStart: { xs: '40px', md: '40px' },
                                          paddingInlineEnd: { xs: '40px', md: '40px' },
                                          color: moral.color,
                                          fontWeight: 600,
                                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                                          mb: 3,
                                          filter: 'brightness(1.3)'
                                      }}
                                  >
                                      {pubText(moral.label)}
                                  </Typography>
                                  <Box
                                      data-swiper-parallax="-100"
                                      sx={{
                                          maxWidth: { xs: '90%', md: '80%' }
                                      }}
                                  >
                                      <Typography
                                          variant="body1"
                                          sx={{
                                              paddingInlineStart: { xs: '40px', md: '40px' },
                                              paddingInlineEnd: { xs: '40px', md: '40px' },
                                              color: 'rgba(255, 255, 255, 0.95)',
                                              fontSize: { xs: '0.9rem', md: '1rem' },
                                              lineHeight: 1.5,
                                              textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8)',
                                          }}
                                      >
                                          {pubText(moral.fullDescription).substring(0, 150)}...
                                      </Typography>
                                  </Box>
                              </Box>
                          </SwiperSlide>
                      )))}
                  </Swiper>
              </Grid>

              {/* Teach Life Lessons Button */}
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMyStoriesClick}
                  endIcon={<KeyboardArrowRight sx={{ fontSize: 24, color: '#fff'}} />}
                  className="my-stories-button"
                  sx={{
                    whiteSpace: 'nowrap',
                    padding: '12px 32px',
                    borderRadius: '32px',
                    background: 'linear-gradient(135deg, #5B8BC7, #4A7BB8)',
                    boxShadow: '0 8px 24px rgba(91, 139, 199, 0.4)',
                    border: 'none',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.05rem',
                    lineHeight: 1.2,
                    minWidth: '200px',
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(91, 139, 199, 0.5)',
                      background: 'linear-gradient(135deg, #4A7BB8, #5B8BC7)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                      boxShadow: '0 4px 8px rgba(91, 139, 199, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.4), 0 1px 2px rgba(91, 139, 199, 0.3)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      left: '-50%',
                      width: '200%',
                      height: '200%',
                      background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)',
                      animation: 'shimmer 3s infinite',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))',
                      borderRadius: '32px 32px 0 0',
                    },
                    '@keyframes shimmer': {
                      '0%': {
                        transform: 'translateX(-100%) translateY(-100%) rotate(45deg)',
                      },
                      '100%': {
                        transform: 'translateX(100%) translateY(100%) rotate(45deg)',
                      },
                    },
                  }}
                >{pubText(" Teach Life Lessons ")}</Button>
              </Grid>
          </Grid>
          </FadeInSection>
          <Grid size={12} className="imageContainer">
              <Box sx={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 4
              }}>
                  {pubText(isSwooshLoading && (
                      <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                          zIndex: 10
                      }}>
                      </Box>
                  ))}
                  <img
                      src={swoosh}
                      alt={pubText("")}
                      className="swoosh"
                      onLoad={handleSwooshLoad}
                      onError={handleSwooshError}
                      style={{
                          opacity: isSwooshLoading ? 0 : 1,
                          transition: 'opacity 0.5s ease-in-out'
                      }}
                  />
              </Box>
          </Grid>
          {/* FAQ Section */}
          <Grid container spacing={4} className="content section" id="faq">
              <Grid size={12} className={"textContainer textContainerRelative"}>
                  <FadeInSection sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <HomeSectionDivider />
                    <Typography variant="h2" className={"title centered"}>{pubText(" Frequently Asked Questions ")}</Typography>
                    <Typography variant="body1" className={"description centered"}>{pubText(
                        " Everything you need to know about Fable, the AI story generator that creates personalized bedtime stories for your kids. "
                    )}</Typography>
                  <Box sx={{
                      mt: 4,
                      mx: 'auto',
                      background: 'transparent',
                      borderRadius: '20px',
                      maxWidth: '900px',
                  }}>
                      {pubText(homepageFAQData.map((faq, index) => (
                          <Accordion
                              key={index}
                              id={faq.anchorId}
                              expanded={expandedFAQ === faq.anchorId}
                              onChange={handleFAQChange(faq.anchorId)}
                              sx={{
                                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
                                  backdropFilter: 'blur(10px)',
                                  border: '1px solid rgba(255, 255, 255, 0.08)',
                                  borderTopLeftRadius: index === 0 ? '20px' : 0,
                                  borderTopRightRadius: index === 0 ? '20px' : 0,
                                  borderBottomLeftRadius: index === homepageFAQData.length - 1 ? '20px' : 0,
                                  borderBottomRightRadius: index === homepageFAQData.length - 1 ? '20px' : 0,
                                  marginBottom: 0,
                                  borderBottom: index !== homepageFAQData.length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
                                  '&:before': {
                                      display: 'none',
                                  },
                                  '&:hover': {
                                      background: 'rgba(255, 255, 255, 0.08)',
                                  },
                              }}
                          >
                              <AccordionSummary
                                  expandIcon={<ExpandMore sx={{ color: '#D8A363' }} />}
                                  sx={{
                                      padding: '16px 20px',
                                      '& .MuiAccordionSummary-content': {
                                          margin: '12px 0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: { xs: 1, sm: 1.25, md: 1.5 },
                                      },
                                  }}
                              >
                                  <Box
                                      component="img"
                                      src={faq.image}
                                      alt={pubText("")}
                                      loading="lazy"
                                      sx={{
                                          width: { xs: 36, sm: 40, md: 44 },
                                          height: { xs: 36, sm: 40, md: 44 },
                                          borderRadius: '50%',
                                          border: '2px solid rgba(216, 163, 99, 0.3)',
                                          boxShadow: '0 2px 8px rgba(216, 163, 99, 0.2)',
                                          objectFit: 'cover',
                                          flexShrink: 0
                                      }}
                                  />
                                  <Typography
                                      variant="h6"
                                      sx={{
                                          color: '#fff',
                                          fontWeight: 600,
                                          fontSize: '1.2rem',
                                          textAlign: "start"
                                      }}
                                  >
                                      {pubText(faq.question)}
                                  </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ padding: '0 20px 20px 20px' }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.25, md: 1.5 } }}>
                                      <Typography
                                          variant="body2"
                                          sx={{
                                              color: 'rgba(255, 255, 255, 0.85)',
                                              fontSize: '1.1rem',
                                              lineHeight: 1.5,
                                              textAlign: "start"
                                          }}
                                      >
                                          {pubText(faq.preview)}
                                      </Typography>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                          <Button
                                              href={pubPath(`/faq/${faq.anchorId}`)}
                                              variant="outlined"
                                              size="small"
                                              endIcon={<KeyboardArrowRight />}
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  navigateWithoutHash(`/faq/${faq.anchorId}`);
                                              }}
                                              sx={{
                                                  borderColor: '#D8A363',
                                                  color: '#D8A363',
                                                  textTransform: 'none',
                                                  fontSize: '0.85rem',
                                                  padding: '4px 12px',
                                                  borderRadius: '16px',
                                                  fontWeight: 500,
                                                  '&:hover': {
                                                      borderColor: '#E8B373',
                                                      color: '#E8B373',
                                                      background: 'rgba(216, 163, 99, 0.1)',
                                                      transform: 'translateX(4px)',
                                                  },
                                              }}
                                          >{pubText(" Read More ")}</Button>
                                      </Box>
                                  </Box>
                              </AccordionDetails>
                          </Accordion>
                      )))}
                  </Box>
                  </FadeInSection>
              </Grid>
          </Grid>
          {/* The shared public footer is rendered by App. Keep this legacy block out of the DOM. */}
          {pubText(false && (
          <>
          <Box sx={{
              pt: { xs: 5, md: 6 },
              pb: 4,
              px: { xs: 3, md: 6 }
          }}>
              <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))'
                  },
                  '@media (min-width: 1024px)': {
                      gridTemplateColumns: 'repeat(7, minmax(0, 1fr))'
                  },
                  gap: { xs: 3, md: 4, lg: 3 },
                  maxWidth: 1200,
                  mx: 'auto',
                  alignItems: 'start',
                  textAlign: "start",
                  '& a': {
                      textDecoration: 'none',
                      display: 'block',
                      mb: 0.9
                  },
                  '& > div > .MuiTypography-root:first-of-type': {
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      lineHeight: 1.2,
                      mb: 1.6
                  },
                  '& a .MuiTypography-root': {
                      color: 'rgba(255,255,255,0.64)',
                      fontSize: '0.82rem',
                      lineHeight: 1.35,
                      transition: 'color 0.18s ease'
                  },
                  '& a:hover .MuiTypography-root': {
                      color: '#D8A363'
                  }
              }}>
                  {/* Explore */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Explore")}</Typography>
                      {pubText([
                          { href: "/stories", text: "Explore AI Stories" },
                          { href: "/narrators", text: "Explore AI Narrators" },
                          { href: "/styles", text: "Explore Art Styles" },
                          { href: "/languages", text: "Explore Languages" },
                          { href: "/morals", text: "Explore Moral Values" },
                          { href: "/musicals", text: "AI Musical Stories" },
                          { href: "/custom-books", text: "Custom Printed Books" },
                          { href: "/videos/narrators", text: "Explore AI Narrator Videos" },
                          { href: "/videos/stories", text: "Explore AI Story Videos" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Art Styles */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Art Styles")}</Typography>
                      {pubText([
                          { href: "/styles/3d-cartoon", text: "3D Cartoon Stories" },
                          { href: "/styles/anime", text: "Anime Stories" },
                          { href: "/styles/watercolor", text: "Watercolor Stories" },
                          { href: "/styles/comic-book", text: "Comic Book Stories" },
                          { href: "/styles/claymation", text: "Claymation Stories" },
                          { href: "/styles/pixel", text: "Pixel Stories" },
                          { href: "/styles/minecraft", text: "Minecraft Stories" },
                          { href: "/styles/photo-realism", text: "Photo Realism Stories" },
                          { href: "/styles/childrens-storybook", text: "Children's Book Stories" },
                          { href: "/styles/classic-blocks", text: "Classic Blocks Stories" },
                          { href: "/styles/spray-paint", text: "Spray Paint Stories" },
                          { href: "/styles/crayon", text: "Crayon Stories" },
                          { href: "/styles/wool", text: "Wool Stories" },
                          { href: "/styles/sugarpop", text: "Sugarpop Stories" },
                          { href: "/styles/origami", text: "Origami Stories" },
                          { href: "/styles/ink-toon", text: "Ink Toon Stories" },
                          { href: "/styles/hd-game", text: "HD Game Stories" },
                          { href: "/styles/cozy-painting", text: "Cozy Painting Stories" },
                          { href: "/styles/sketch", text: "Sketch Stories" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Narrators */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Narrators")}</Typography>
                      {pubText([
                          { href: "/narrators/sir-albus", text: "Sir Albus" },
                          { href: "/narrators/sage", text: "Sage" },
                          { href: "/narrators/optimus", text: "Optimus" },
                          { href: "/narrators/ms-walker", text: "Ms. Walker" },
                          { href: "/narrators/king-arthur", text: "King Arthur" },
                          { href: "/narrators/penny-snow", text: "Penny Snow" },
                          { href: "/narrators/ash", text: "Ash" },
                          { href: "/narrators/queen-bee", text: "Queen Bee" },
                          { href: "/narrators/cherry-twinkle", text: "Cherry Twinkle" },
                          { href: "/narrators/aunt-beth", text: "Aunt Beth" },
                          { href: "/narrators/lord-ragnar", text: "Lord Ragnar" },
                          { href: "/narrators/captain-flint", text: "Captain Flint" },
                          { href: "/narrators/nimble", text: "Nimble" },
                          { href: "/narrators/master-percy", text: "Master Percy" },
                          { href: "/narrators/juni", text: "Juni" },
                          { href: "/narrators/detective-gadget", text: "Detective Gadget" },
                          { href: "/narrators/atlas", text: "Atlas" },
                          { href: "/narrators/orin-stormvale", text: "Orin Stormvale" },
                          { href: "/narrators/boots", text: "Boots" },
                          { href: "/narrators/lucian", text: "Lucian" },
                          { href: "/narrators/lucky-mcguire", text: "Lucky McGuire" },
                          { href: "/narrators/fiona", text: "Fiona" },
                          { href: "/narrators/dexter-dynamite", text: "Dexter Dynamite" },
                          { href: "/narrators/finn", text: "Finn" },
                          { href: "/narrators/princess-anna", text: "Princess Anna" },
                          { href: "/narrators/ruby", text: "Ruby" },
                          { href: "/narrators/polly", text: "Polly" },
                          { href: "/narrators/empress-khali", text: "Empress Khali" },
                          { href: "/narrators/amelia", text: "Amelia" },
                          { href: "/narrators/harvey-quinn", text: "Harvey Quinn" },
                          { href: "/narrators/dash", text: "Dash" },
                          { href: "/narrators/britney", text: "Britney" },
                          { href: "/narrators/malrog", text: "Malrog" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Languages */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Languages")}</Typography>
                      {pubText([
                          { href: "/languages/english", text: "English Stories" },
                          { href: "/languages/spanish", text: "Spanish Stories" },
                          { href: "/languages/french", text: "French Stories" },
                          { href: "/languages/german", text: "German Stories" },
                          { href: "/languages/italian", text: "Italian Stories" },
                          { href: "/languages/portuguese", text: "Portuguese Stories" },
                          { href: "/languages/chinese", text: "Chinese Stories" },
                          { href: "/languages/japanese", text: "Japanese Stories" },
                          { href: "/languages/korean", text: "Korean Stories" },
                          { href: "/languages/arabic", text: "Arabic Stories" },
                          { href: "/languages/hindi", text: "Hindi Stories" },
                          { href: "/languages/russian", text: "Russian Stories" },
                          { href: "/languages/bulgarian", text: "Bulgarian Stories" },
                          { href: "/languages/czech", text: "Czech Stories" },
                          { href: "/languages/dutch", text: "Dutch Stories" },
                          { href: "/languages/finnish", text: "Finnish Stories" },
                          { href: "/languages/greek", text: "Greek Stories" },
                          { href: "/languages/indonesian", text: "Indonesian Stories" },
                          { href: "/languages/polish", text: "Polish Stories" },
                          { href: "/languages/romanian", text: "Romanian Stories" },
                          { href: "/languages/thai", text: "Thai Stories" },
                          { href: "/languages/turkish", text: "Turkish Stories" },
                          { href: "/languages/ukrainian", text: "Ukrainian Stories" },
                          { href: "/languages/vietnamese", text: "Vietnamese Stories" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Moral Values */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Moral Values")}</Typography>
                      {pubText([
                          { href: "/morals/integrity", text: "Integrity" },
                          { href: "/morals/perseverance", text: "Perseverance" },
                          { href: "/morals/patience", text: "Patience" },
                          { href: "/morals/reliability", text: "Reliability" },
                          { href: "/morals/kindness", text: "Kindness" },
                          { href: "/morals/forgiveness", text: "Forgiveness" },
                          { href: "/morals/truthfulness", text: "Truthfulness" },
                          { href: "/morals/respect", text: "Respect" },
                          { href: "/morals/friendship", text: "Friendship" },
                          { href: "/morals/mindfulness", text: "Mindfulness" },
                          { href: "/morals/contentment", text: "Contentment" },
                          { href: "/morals/sharing", text: "Sharing" },
                          { href: "/morals/fairness", text: "Fairness" },
                          { href: "/morals/example", text: "Being a Good Example" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Resources */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Resources")}</Typography>
                      {pubText([
                          { href: "/blog", text: "Blog" },
                          { href: "/faq", text: "Frequently Asked Questions" },
                          { href: "/support", text: "Support" },
                          { href: "/create-story-books-with-ai", text: "Create AI Story Books" },
                          { href: "/blog/best-ai-storytelling-apps-2026", text: "Top 10 AI Storytelling Apps" },
                          { href: "/blog/how-to-create-personalized-childrens-book", text: "Create a Personalized Book" },
                          { href: "/blog/print-your-fable-story-complete-guide", text: "Print Your Story" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>

                  {/* Company */}
                  <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>{pubText("Company")}</Typography>
                      {pubText([
                          { href: "/payment", text: "Pricing & Plans" },
                          { href: "/privacy", text: "Privacy Policy" },
                          { href: "/terms-of-service", text: "Terms of Service" },
                      ].map(link => (
                          <a key={link.href} href={pubPath(link.href)} style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', '&:hover': { color: '#D8A363' } }}>{pubText(link.text)}</Typography>
                          </a>
                      )))}
                  </Box>
              </Box>
          </Box>

          <Typography className={"centered"} sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', pb: 2 }}>{pubText(" Fable, 2026 ")}</Typography>
          </>
          ))}
          <Box sx={{px:3,pb:4}}><PublicLanguageLinks /></Box></Box>
  );
});

export default HomePage;
